import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronDown, ChevronsLeftRight, ChevronsRightLeft, ChevronsUpDown, Columns3, Copy, Eye, EyeOff, ListFilter, Lock, LockOpen, MoreVertical, Pencil, Plus, RotateCcw, Save, Search, Table2, Trash2, X } from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useProductData } from '../data'
import { authFetch } from '../auth'
import {
  accessoryFor,
  costVersionOn,
  effectiveFulfillmentMode,
  estimatedProfitForSku,
  internalProductCost,
  internalProductCostOn,
  inventoryCostModeOn,
  JD_STORE_ID,
  profitWithRebateFor,
  productMasterCostDefaults,
  productMasterRecordMap,
  rebateAmountFor,
  rebateRateFor,
  resolvedProductCost,
  shippingProviderFor,
} from '../productMaster'
import { skuTagAssignments } from '../segments'
import type {
  FulfillmentMode,
  InternalProductInfoRecord,
  InventoryCostMode,
  ProductMasterDefaults,
  ProductMasterRecord,
  ProductRecord,
  ShippingProvider,
} from '../types'
import { money, percent } from '../utils'

type CostFilter = 'all' | 'recorded' | 'missing'
type SkuFilterType = 'text' | 'number' | 'date' | 'presence'
type SkuFilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'before'
  | 'after'
  | 'isEmpty'
  | 'isNotEmpty'

type SkuColumnId =
  | 'rowNumber'
  | 'image'
  | 'productName'
  | 'spu'
  | 'jdSku'
  | 'merchantSku'
  | 'erpAvailable'
  | 'companyProductName'
  | 'barcode'
  | 'generalAgentPrice'
  | 'brandSpecialPrice'
  | 'productCost'
  | 'inventoryCostMode'
  | 'inventoryCostHistory'
  | 'rebateRate'
  | 'rebateAmount'
  | 'provincialAgentPrice'
  | 'ecommercePrice'
  | 'ip'
  | 'capacity'
  | 'cupType'
  | 'brand'
  | 'material'
  | 'audience'
  | 'recordDate'
  | 'accessory'
  | 'listed'
  | 'packagingCost'
  | 'shippingProvider'
  | 'shippingFee'
  | 'giftCost'
  | 'shippingInsuranceFee'
  | 'platformFeeRate'
  | 'taxRate'
  | 'tags'
  | 'effectiveDate'
  | 'gmv'
  | 'soldItemCount'
  | 'profit'
  | 'profitWithRebate'
  | 'profitWithRebateMargin'
  | 'margin'

interface SkuColumnDefinition {
  id: SkuColumnId
  label: string
  width: number
  locked?: boolean
}

interface SkuFilterCondition {
  id: number
  field: SkuColumnId
  operator: SkuFilterOperator
  value: string
}

type SkuSortDirection = 'asc' | 'desc'

interface SkuSortRule {
  field: SkuColumnId
  direction: SkuSortDirection
}

interface SkuViewConfig {
  search: string
  tagId: string
  costFilter: CostFilter
  filters: Array<Omit<SkuFilterCondition, 'id'>>
  sort: SkuSortRule | null
  hiddenColumns: SkuColumnId[]
  columnWidths: SkuColumnWidths
  columnOrder: SkuColumnId[]
}

interface SavedSkuView {
  id: string
  name: string
  config: SkuViewConfig
  locked: boolean
}

interface RowDraft {
  skuId: string
  isListed: boolean
  fulfillmentMode: FulfillmentMode
  tagIds: string[]
  packagingCost: string
  shippingProvider: ShippingProvider
  shippingFee: string
  platformFeeRate: string
  taxRate: string
  effectiveDate: string
  note: string
}

interface BatchRow {
  skuId: string
  isListed?: boolean
  fulfillmentMode: FulfillmentMode
  tagIds: string[]
  packagingCost: number | null
  shippingProvider: ShippingProvider
  shippingFee: number | null
  platformFeeRate: number | null
  taxRate: number | null
  effectiveDate: string
  note: string
}

const SKU_COLUMNS: SkuColumnDefinition[] = [
  { id: 'rowNumber', label: '序号', width: 52, locked: true },
  { id: 'image', label: '图片', width: 72 },
  { id: 'productName', label: '商品名称', width: 240, locked: true },
  { id: 'spu', label: 'SPU', width: 140 },
  { id: 'jdSku', label: '京东SKU', width: 160 },
  { id: 'merchantSku', label: '商家SKU', width: 150 },
  { id: 'soldItemCount', label: '成交件数', width: 100 },
  { id: 'erpAvailable', label: 'ERP库存', width: 100 },
  { id: 'companyProductName', label: '公司品名', width: 220 },
  { id: 'barcode', label: '条码', width: 150 },
  { id: 'generalAgentPrice', label: '总代价', width: 100 },
  { id: 'brandSpecialPrice', label: '品牌特价', width: 110 },
  { id: 'productCost', label: '产品成本', width: 110 },
  { id: 'inventoryCostMode', label: '库存成本', width: 120 },
  { id: 'inventoryCostHistory', label: '切换记录', width: 110 },
  { id: 'rebateRate', label: '返点', width: 84 },
  { id: 'provincialAgentPrice', label: '新省代价', width: 100 },
  { id: 'ecommercePrice', label: '电商价', width: 100 },
  { id: 'ip', label: 'IP', width: 100 },
  { id: 'capacity', label: '容量', width: 80 },
  { id: 'cupType', label: '杯型', width: 220 },
  { id: 'brand', label: '品牌', width: 100 },
  { id: 'material', label: '材质', width: 100 },
  { id: 'audience', label: '人群', width: 90 },
  { id: 'recordDate', label: '公司资料日期', width: 130 },
  { id: 'accessory', label: '配件', width: 84 },
  { id: 'listed', label: '是否上架', width: 100 },
  { id: 'packagingCost', label: '包装成本', width: 110 },
  { id: 'shippingProvider', label: '快递', width: 150 },
  { id: 'shippingFee', label: '运费', width: 90 },
  { id: 'giftCost', label: '赠品', width: 90 },
  { id: 'shippingInsuranceFee', label: '运费险', width: 90 },
  { id: 'platformFeeRate', label: '平台扣点', width: 100 },
  { id: 'taxRate', label: '税点', width: 90 },
  { id: 'tags', label: '商品标签', width: 190 },
  { id: 'effectiveDate', label: '生效日期', width: 130 },
  { id: 'gmv', label: '昨日成交', width: 110 },
  { id: 'profit', label: '预估利润', width: 110 },
  { id: 'rebateAmount', label: '返点金额', width: 110 },
  { id: 'profitWithRebate', label: '含返点利润', width: 120 },
  { id: 'profitWithRebateMargin', label: '含返点利润率', width: 130 },
  { id: 'margin', label: '预估毛利率', width: 120 },
]

const SKU_COLUMN_VISIBILITY_STORAGE_KEY = 'product-master-sku-hidden-columns-v2'
const SKU_COLUMN_WIDTHS_STORAGE_KEY = 'product-master-sku-column-widths-v1'
const SKU_DEFAULT_VIEW_STORAGE_KEY = 'product-master-sku-default-view-v1'
const SKU_COLUMN_IDS = new Set<SkuColumnId>(SKU_COLUMNS.map((column) => column.id))
const MIN_SKU_COLUMN_WIDTH = 64
const MAX_SKU_COLUMN_WIDTH = 520
const SKU_ROW_HEIGHT = 61
const SKU_VIRTUAL_OVERSCAN = 8
type SkuColumnWidths = Partial<Record<SkuColumnId, number>>
type SkuSummaryMode = 'none' | 'count' | 'sum'
type SkuSummaryModes = Partial<Record<SkuColumnId, SkuSummaryMode>>
const NUMBER_FILTER_FIELDS = new Set<SkuColumnId>([
  'generalAgentPrice',
  'brandSpecialPrice',
  'productCost',
  'rebateRate',
  'rebateAmount',
  'provincialAgentPrice',
  'ecommercePrice',
  'capacity',
  'erpAvailable',
  'packagingCost',
  'shippingFee',
  'giftCost',
  'shippingInsuranceFee',
  'platformFeeRate',
  'taxRate',
  'gmv',
  'soldItemCount',
  'profit',
  'profitWithRebate',
  'profitWithRebateMargin',
  'margin',
])
const SUMMABLE_SKU_FIELDS = new Set<SkuColumnId>(
  Array.from(NUMBER_FILTER_FIELDS).filter((field) =>
    field !== 'rebateRate'
    && field !== 'profitWithRebateMargin'
    && field !== 'erpAvailable'),
)
const SKU_SUMMARY_STORAGE_KEY = 'product-master-sku-summary-modes-v1'
const SKU_SAVED_VIEWS_STORAGE_KEY = 'product-master-sku-saved-views-v2'
const SKU_LEGACY_SAVED_VIEWS_STORAGE_KEY = 'product-master-sku-saved-views-v1'
const SKU_ACTIVE_SAVED_VIEW_STORAGE_KEY = 'product-master-sku-active-saved-view-v1'
const MONEY_SUMMARY_FIELDS = new Set<SkuColumnId>([
  'generalAgentPrice',
  'brandSpecialPrice',
  'productCost',
  'rebateAmount',
  'provincialAgentPrice',
  'ecommercePrice',
  'packagingCost',
  'shippingFee',
  'giftCost',
  'shippingInsuranceFee',
  'gmv',
  'profit',
  'profitWithRebate',
])
const PERCENT_SUMMARY_FIELDS = new Set<SkuColumnId>([
  'platformFeeRate',
  'taxRate',
  'profitWithRebateMargin',
  'margin',
])
const FILTER_OPERATORS: Record<SkuFilterType, Array<{ value: SkuFilterOperator; label: string }>> = {
  text: [
    { value: 'equals', label: '等于' },
    { value: 'notEquals', label: '不等于' },
    { value: 'contains', label: '包含' },
    { value: 'notContains', label: '不包含' },
    { value: 'isEmpty', label: '为空' },
    { value: 'isNotEmpty', label: '不为空' },
  ],
  number: [
    { value: 'equals', label: '等于' },
    { value: 'notEquals', label: '不等于' },
    { value: 'greaterThan', label: '大于' },
    { value: 'greaterThanOrEqual', label: '大于等于' },
    { value: 'lessThan', label: '小于' },
    { value: 'lessThanOrEqual', label: '小于等于' },
    { value: 'isEmpty', label: '为空' },
    { value: 'isNotEmpty', label: '不为空' },
  ],
  date: [
    { value: 'equals', label: '等于' },
    { value: 'before', label: '早于' },
    { value: 'after', label: '晚于' },
    { value: 'isEmpty', label: '为空' },
    { value: 'isNotEmpty', label: '不为空' },
  ],
  presence: [
    { value: 'isEmpty', label: '为空' },
    { value: 'isNotEmpty', label: '不为空' },
  ],
}
const SKU_FILTER_OPERATOR_IDS = new Set<SkuFilterOperator>(
  Object.values(FILTER_OPERATORS).flatMap((operators) => operators.map((operator) => operator.value)),
)
const FILTER_MULTI_VALUE_SEPARATOR = '\u001f'
const MULTI_SELECT_FILTER_FIELDS = new Set<SkuColumnId>(['tags'])
const splitFilterValues = (value: string) =>
  value.split(FILTER_MULTI_VALUE_SEPARATOR).map((item) => item.trim()).filter(Boolean)
const SORTABLE_SKU_COLUMNS = SKU_COLUMNS.filter((column) =>
  column.id !== 'rowNumber' && column.id !== 'image',
)
const HEADER_SORTABLE_SKU_FIELDS = NUMBER_FILTER_FIELDS
const filterTypeForColumn = (field: SkuColumnId): SkuFilterType => {
  if (field === 'image') return 'presence'
  if (field === 'effectiveDate' || field === 'recordDate') return 'date'
  if (NUMBER_FILTER_FIELDS.has(field)) return 'number'
  return 'text'
}
const defaultOperatorForColumn = (field: SkuColumnId): SkuFilterOperator =>
  filterTypeForColumn(field) === 'presence' ? 'isNotEmpty' : 'equals'
const filterOperatorNeedsValue = (operator: SkuFilterOperator) =>
  operator !== 'isEmpty' && operator !== 'isNotEmpty'
const createFilterCondition = (id: number, field: SkuColumnId = 'productName'): SkuFilterCondition => ({
  id,
  field,
  operator: defaultOperatorForColumn(field),
  value: '',
})
const filterConditionIsActive = (condition: SkuFilterCondition) =>
  !filterOperatorNeedsValue(condition.operator) || condition.value.trim() !== ''
const matchesSkuFilter = (
  values: Array<string | number | null | undefined>,
  condition: SkuFilterCondition,
) => {
  const presentValues = values.filter((value): value is string | number =>
    value !== null && value !== undefined && String(value).trim() !== '',
  )
  if (condition.operator === 'isEmpty') return presentValues.length === 0
  if (condition.operator === 'isNotEmpty') return presentValues.length > 0
  if (!condition.value.trim()) return true

  const type = filterTypeForColumn(condition.field)
  if (type === 'number') {
    const expected = Number(condition.value)
    if (!Number.isFinite(expected)) return false
    return condition.operator === 'notEquals'
      ? presentValues.every((value) => Number(value) !== expected)
      : presentValues.some((value) => {
        const numericValue = Number(value)
        if (!Number.isFinite(numericValue)) return false
        if (condition.operator === 'greaterThan') return numericValue > expected
        if (condition.operator === 'greaterThanOrEqual') return numericValue >= expected
        if (condition.operator === 'lessThan') return numericValue < expected
        if (condition.operator === 'lessThanOrEqual') return numericValue <= expected
        return numericValue === expected
      })
  }

  const normalized = presentValues.map((value) => String(value).toLowerCase())
  const expectedValues = splitFilterValues(condition.value).map((value) => value.toLowerCase())
  if (expectedValues.length > 1 || MULTI_SELECT_FILTER_FIELDS.has(condition.field)) {
    const matchesAny = expectedValues.some((expected) =>
      normalized.some((value) =>
        condition.operator === 'contains' || condition.operator === 'notContains'
          ? value.includes(expected)
          : value === expected,
      ),
    )
    return condition.operator === 'notEquals' || condition.operator === 'notContains'
      ? !matchesAny
      : matchesAny
  }
  const expected = expectedValues[0] || ''
  if (condition.operator === 'notEquals') return normalized.every((value) => value !== expected)
  if (condition.operator === 'contains') return normalized.some((value) => value.includes(expected))
  if (condition.operator === 'notContains') return normalized.every((value) => !value.includes(expected))
  if (condition.operator === 'before') return normalized.some((value) => value < expected)
  if (condition.operator === 'after') return normalized.some((value) => value > expected)
  return normalized.some((value) => value === expected)
}
const defaultHiddenSkuColumns = () => new Set<SkuColumnId>()
const validHiddenSkuColumns = (value: unknown) => {
  if (!Array.isArray(value)) return []
  const requested = new Set(value)
  return SKU_COLUMNS
    .filter((column) => !column.locked && requested.has(column.id))
    .map((column) => column.id)
}
const initialHiddenSkuColumns = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SKU_COLUMN_VISIBILITY_STORAGE_KEY) || '[]')
    return new Set<SkuColumnId>(validHiddenSkuColumns(stored))
  } catch {
    return defaultHiddenSkuColumns()
  }
}
const validSkuColumnWidths = (value: unknown): SkuColumnWidths => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const stored = value as Record<string, unknown>
  return Object.fromEntries(
    SKU_COLUMNS.flatMap((column) => {
      const width = stored[column.id]
      return typeof width === 'number'
        && Number.isFinite(width)
        && width >= MIN_SKU_COLUMN_WIDTH
        && width <= MAX_SKU_COLUMN_WIDTH
        ? [[column.id, width]]
        : []
    }),
  ) as SkuColumnWidths
}
const validSkuColumnOrder = (value: unknown): SkuColumnId[] => {
  if (!Array.isArray(value)) return []
  return value.filter((columnId, index, items): columnId is SkuColumnId =>
    typeof columnId === 'string'
    && SKU_COLUMN_IDS.has(columnId as SkuColumnId)
    && items.indexOf(columnId) === index,
  )
}
const initialSkuColumnWidths = (): SkuColumnWidths => {
  try {
    const stored = JSON.parse(localStorage.getItem(SKU_COLUMN_WIDTHS_STORAGE_KEY) || '{}')
    return validSkuColumnWidths(stored)
  } catch {
    return {}
  }
}
const initialSkuSummaryModes = (): SkuSummaryModes => {
  const defaults: SkuSummaryModes = { productName: 'count' }
  try {
    const stored = JSON.parse(localStorage.getItem(SKU_SUMMARY_STORAGE_KEY) || '{}')
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return defaults
    const validEntries = Object.entries(stored).filter(([columnId, mode]) =>
      columnId !== 'rowNumber'
      && SKU_COLUMN_IDS.has(columnId as SkuColumnId)
      && (mode === 'none' || mode === 'count' || (mode === 'sum' && SUMMABLE_SKU_FIELDS.has(columnId as SkuColumnId))),
    )
    return { ...defaults, ...Object.fromEntries(validEntries) } as SkuSummaryModes
  } catch {
    return defaults
  }
}
const emptySkuViewConfig = (): SkuViewConfig => ({
  search: '',
  tagId: '',
  costFilter: 'all',
  filters: [],
  sort: null,
  hiddenColumns: [],
  columnWidths: {},
  columnOrder: [],
})
const parseSkuViewConfig = (config: unknown, fallbackHidden: SkuColumnId[], fallbackWidths: SkuColumnWidths): SkuViewConfig | null => {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return null
  const value = config as Record<string, unknown>
  const filters = Array.isArray(value.filters)
    ? value.filters.filter((filter: unknown): filter is Omit<SkuFilterCondition, 'id'> => {
      if (!filter || typeof filter !== 'object' || Array.isArray(filter)) return false
      const candidate = filter as Partial<SkuFilterCondition>
      return typeof candidate.field === 'string'
        && candidate.field !== 'rowNumber'
        && SKU_COLUMN_IDS.has(candidate.field as SkuColumnId)
        && typeof candidate.operator === 'string'
        && SKU_FILTER_OPERATOR_IDS.has(candidate.operator as SkuFilterOperator)
        && typeof candidate.value === 'string'
    })
    : []
  const sort = value.sort
    && typeof value.sort === 'object'
    && !Array.isArray(value.sort)
    && typeof (value.sort as { field?: unknown }).field === 'string'
    && SORTABLE_SKU_COLUMNS.some((column) => column.id === (value.sort as { field: string }).field)
    && ((value.sort as { direction?: unknown }).direction === 'asc' || (value.sort as { direction?: unknown }).direction === 'desc')
    ? {
      field: (value.sort as { field: SkuColumnId }).field,
      direction: (value.sort as { direction: SkuSortDirection }).direction,
    }
    : null
  return {
    search: typeof value.search === 'string' ? value.search : '',
    tagId: typeof value.tagId === 'string' ? value.tagId : '',
    costFilter: value.costFilter === 'recorded' || value.costFilter === 'missing' ? value.costFilter : 'all',
    filters,
    sort,
    hiddenColumns: validHiddenSkuColumns(value.hiddenColumns ?? fallbackHidden),
    columnWidths: validSkuColumnWidths(value.columnWidths ?? fallbackWidths),
    columnOrder: validSkuColumnOrder(value.columnOrder),
  }
}
const initialDefaultSkuViewConfig = (): SkuViewConfig => {
  const legacyHiddenColumns = Array.from(initialHiddenSkuColumns())
  const legacyColumnWidths = initialSkuColumnWidths()
  try {
    const stored = JSON.parse(localStorage.getItem(SKU_DEFAULT_VIEW_STORAGE_KEY) || 'null')
    const parsed = parseSkuViewConfig(stored, legacyHiddenColumns, legacyColumnWidths)
    if (parsed) return parsed
  } catch {
    // fall through to legacy defaults
  }
  return {
    ...emptySkuViewConfig(),
    hiddenColumns: legacyHiddenColumns,
    columnWidths: legacyColumnWidths,
  }
}
const initialSavedSkuViews = (): SavedSkuView[] => {
  try {
    const legacyHiddenColumns = Array.from(initialHiddenSkuColumns())
    const legacyColumnWidths = initialSkuColumnWidths()
    const stored = JSON.parse(
      localStorage.getItem(SKU_SAVED_VIEWS_STORAGE_KEY)
      || localStorage.getItem(SKU_LEGACY_SAVED_VIEWS_STORAGE_KEY)
      || '[]',
    )
    if (!Array.isArray(stored)) return []
    return stored.flatMap((item): SavedSkuView[] => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return []
      const id = typeof item.id === 'string' ? item.id : ''
      const name = typeof item.name === 'string' ? item.name.trim() : ''
      const config = parseSkuViewConfig(item.config, legacyHiddenColumns, legacyColumnWidths)
      if (!id || !name || !config) return []
      return [{
        id,
        name,
        locked: item.locked === true,
        config,
      }]
    })
  } catch {
    return []
  }
}
const initialActiveSavedViewId = () => {
  const stored = localStorage.getItem(SKU_ACTIVE_SAVED_VIEW_STORAGE_KEY)
  return stored && stored !== 'default' ? stored : 'default'
}
const PRESET_SKU_VIEW_ID_PREFIX = 'sku-preset-'
const isPresetSkuViewId = (viewId: string) => viewId.startsWith(PRESET_SKU_VIEW_ID_PREFIX)
const presetSkuViews = (base: SkuViewConfig): SavedSkuView[] => [
  {
    id: `${PRESET_SKU_VIEW_ID_PREFIX}sold`,
    name: '有销量',
    locked: true,
    config: {
      ...base,
      search: '',
      tagId: '',
      costFilter: 'all',
      filters: [{ field: 'soldItemCount', operator: 'greaterThan', value: '0' }],
      sort: { field: 'soldItemCount', direction: 'desc' },
    },
  },
  {
    id: `${PRESET_SKU_VIEW_ID_PREFIX}sold-no-accessory`,
    name: '有销量·非配件',
    locked: true,
    config: {
      ...base,
      search: '',
      tagId: '',
      costFilter: 'all',
      filters: [
        { field: 'soldItemCount', operator: 'greaterThan', value: '0' },
        { field: 'accessory', operator: 'equals', value: '否' },
      ],
      sort: { field: 'soldItemCount', direction: 'desc' },
    },
  },
  {
    id: `${PRESET_SKU_VIEW_ID_PREFIX}sold-missing-cost`,
    name: '成交缺成本',
    locked: true,
    config: {
      ...base,
      search: '',
      tagId: '',
      costFilter: 'all',
      filters: [
        { field: 'gmv', operator: 'greaterThan', value: '0' },
        { field: 'productCost', operator: 'isEmpty', value: '' },
      ],
      sort: { field: 'gmv', direction: 'desc' },
      hiddenColumns: base.hiddenColumns.filter((columnId) =>
        !['jdSku', 'merchantSku', 'companyProductName', 'productCost', 'gmv', 'soldItemCount'].includes(columnId),
      ),
    },
  },
]
const formatSkuSummaryValue = (field: SkuColumnId, value: number) => {
  if (MONEY_SUMMARY_FIELDS.has(field)) return value.toFixed(2)
  if (PERCENT_SUMMARY_FIELDS.has(field)) return `${value.toFixed(2)}%`
  return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

const optionalNumber = (value: string) => value.trim() === '' ? null : Number(value)
const shanghaiToday = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Shanghai',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(new Date())
const displayCost = (value: number | null | undefined) => value === null || value === undefined ? '—' : value.toFixed(2)
const inventoryCostModeLabel = (mode: InventoryCostMode) => mode === 'brand-special' ? '特价库存' : '总代库存'
const shippingLabel = (provider: ShippingProvider) =>
  provider === 'jd-warehouse' ? '京东入仓' : provider === 'yto-express' ? '圆通快递' : '京东快递'
const defaultShippingFee = (provider: ShippingProvider, defaults: ProductMasterDefaults) =>
  provider === 'jd-warehouse' ? defaults.jdWarehouseFee : provider === 'yto-express' ? defaults.ytoShippingFee : defaults.jdShippingFee
const distinctValues = <T,>(
  records: InternalProductInfoRecord[],
  read: (record: InternalProductInfoRecord) => T | null | '',
  format: (value: T) => string = String,
) => {
  const values = Array.from(new Set(
    records.map(read).filter((value): value is T => value !== null && value !== ''),
  ))
  return values.length ? values.map(format).join(' / ') : '—'
}
const internalPrice = (
  records: InternalProductInfoRecord[],
  key: 'generalAgentPrice' | 'provincialAgentPrice' | 'ecommercePrice',
) => distinctValues(records, (record) => record[key], money)
const listedFor = (
  product: ProductRecord,
  record: ProductMasterRecord | undefined,
) => record?.isListed ?? product.isListed ?? Boolean(product.merchantSku || product.productUrl)
const batchRowForDraft = (draft: RowDraft): BatchRow => ({
  skuId: draft.skuId,
  isListed: draft.isListed,
  fulfillmentMode: draft.fulfillmentMode,
  tagIds: draft.tagIds,
  packagingCost: optionalNumber(draft.packagingCost),
  shippingProvider: draft.shippingProvider,
  shippingFee: optionalNumber(draft.shippingFee),
  platformFeeRate: optionalNumber(draft.platformFeeRate),
  taxRate: optionalNumber(draft.taxRate),
  effectiveDate: draft.effectiveDate,
  note: draft.note,
})
const draftsMatch = (left: RowDraft, right: RowDraft) =>
  left.skuId === right.skuId
  && left.isListed === right.isListed
  && left.fulfillmentMode === right.fulfillmentMode
  && left.packagingCost === right.packagingCost
  && left.shippingProvider === right.shippingProvider
  && left.shippingFee === right.shippingFee
  && left.platformFeeRate === right.platformFeeRate
  && left.taxRate === right.taxRate
  && left.effectiveDate === right.effectiveDate
  && left.note === right.note
  && left.tagIds.length === right.tagIds.length
  && left.tagIds.every((tagId, index) => tagId === right.tagIds[index])

function draftFor(
  product: ProductRecord,
  record: ProductMasterRecord | undefined,
  inheritedJdWarehouse: boolean,
  selectedDate: string,
  defaults: ProductMasterDefaults,
  isAccessory: boolean,
  isBrandSpecial: boolean,
  isListed: boolean,
): RowDraft {
  const cost = costVersionOn(record, selectedDate)
  const shippingProvider = shippingProviderFor(
    record,
    inheritedJdWarehouse,
    selectedDate,
    defaults,
    isAccessory,
    isBrandSpecial,
  )
  const resolved = resolvedProductCost(
    record,
    selectedDate,
    defaults,
    shippingProvider,
    isAccessory || isBrandSpecial,
  )
  return {
    skuId: product.id,
    isListed,
    fulfillmentMode: shippingProvider === 'jd-warehouse' ? 'jd-warehouse' : 'merchant-direct',
    tagIds: record?.tagIds || [],
    packagingCost: resolved.packagingCost.toString(),
    shippingProvider,
    shippingFee: resolved.shippingFee.toString(),
    platformFeeRate: resolved.platformFeeRate.toString(),
    taxRate: resolved.taxRate.toString(),
    effectiveDate: cost?.effectiveDate || selectedDate,
    note: record?.note || '',
  }
}

export function ProductMasterPage() {
  const {
    data,
    erpInventory,
    productMaster,
    internalProductInfo,
    segmentIndex,
    selectedDate,
    reloadProductMaster,
    reloadSegments,
  } = useProductData()
  const [search, setSearch] = useState('')
  const [tagId, setTagId] = useState('')
  const [costFilter, setCostFilter] = useState<CostFilter>('all')
  const [editUnlocked, setEditUnlocked] = useState(false)
  const [drafts, setDrafts] = useState<Map<string, RowDraft>>(() => new Map())
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [tableScrollTop, setTableScrollTop] = useState(0)
  const [tableScrollLeft, setTableScrollLeft] = useState(0)
  const [stickyToolbarHeight, setStickyToolbarHeight] = useState(0)
  const [stickyToolbarTop, setStickyToolbarTop] = useState(50)
  const [summaryDock, setSummaryDock] = useState({ visible: false, left: 0, width: 0 })
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight)
  const [defaultViewConfig, setDefaultViewConfig] = useState<SkuViewConfig>(initialDefaultSkuViewConfig)
  const [fieldPanelOpen, setFieldPanelOpen] = useState(false)
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [openFilterOptionsId, setOpenFilterOptionsId] = useState<number | null>(null)
  const [filterValueQuery, setFilterValueQuery] = useState('')
  const [sortPanelOpen, setSortPanelOpen] = useState(false)
  const [filterConditions, setFilterConditions] = useState<SkuFilterCondition[]>(() => [createFilterCondition(1)])
  const [sortRule, setSortRule] = useState<SkuSortRule | null>(null)
  const [savedViews, setSavedViews] = useState<SavedSkuView[]>(initialSavedSkuViews)
  const [activeSavedViewId, setActiveSavedViewId] = useState(initialActiveSavedViewId)
  const [createViewOpen, setCreateViewOpen] = useState(false)
  const [newViewName, setNewViewName] = useState('')
  const [openSavedViewMenuId, setOpenSavedViewMenuId] = useState('')
  const [savedViewMenuPosition, setSavedViewMenuPosition] = useState({ top: 0, left: 0 })
  const [pendingRenameViewId, setPendingRenameViewId] = useState('')
  const [renameViewName, setRenameViewName] = useState('')
  const [pendingDeleteViewId, setPendingDeleteViewId] = useState('')
  const [viewMessage, setViewMessage] = useState('')
  const [hiddenColumns, setHiddenColumns] = useState<Set<SkuColumnId>>(
    () => new Set(defaultViewConfig.hiddenColumns),
  )
  const [columnWidths, setColumnWidths] = useState<SkuColumnWidths>(
    () => ({ ...defaultViewConfig.columnWidths }),
  )
  const [columnOrder, setColumnOrder] = useState<SkuColumnId[]>(
    () => [...defaultViewConfig.columnOrder],
  )
  const [draggedColumnId, setDraggedColumnId] = useState<SkuColumnId | ''>('')
  const [columnDropTarget, setColumnDropTarget] = useState<{ id: SkuColumnId; position: 'before' | 'after' } | null>(null)
  const [summaryModes, setSummaryModes] = useState<SkuSummaryModes>(initialSkuSummaryModes)
  const nextFilterId = useRef(2)
  const initialSavedViewAppliedRef = useRef(false)
  const [copiedSkuId, setCopiedSkuId] = useState('')
  const copySkuFeedbackTimer = useRef(0)
  const workspaceRef = useRef<HTMLDivElement>(null)
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const toolbarRef = useRef<HTMLElement>(null)
  const fieldControlRef = useRef<HTMLDivElement>(null)
  const filterControlRef = useRef<HTMLDivElement>(null)
  const sortControlRef = useRef<HTMLDivElement>(null)
  const savedViewBarRef = useRef<HTMLElement>(null)
  const windowScrollFrameRef = useRef<number | null>(null)
  const columnPointerDragRef = useRef<{ id: SkuColumnId; startX: number; startY: number; active: boolean } | null>(null)
  const columnDropTargetRef = useRef<{ id: SkuColumnId; position: 'before' | 'after' } | null>(null)

  useEffect(() => {
    if (!fieldPanelOpen && !filterPanelOpen && !sortPanelOpen) return

    const closeToolbarPanels = () => {
      setFieldPanelOpen(false)
      setFilterPanelOpen(false)
      setOpenFilterOptionsId(null)
      setFilterValueQuery('')
      setSortPanelOpen(false)
    }
    const handlePointerDown = (event: PointerEvent) => {
      const activeControl = fieldPanelOpen
        ? fieldControlRef.current
        : filterPanelOpen
          ? filterControlRef.current
          : sortControlRef.current
      if (activeControl && event.composedPath().includes(activeControl)) return
      closeToolbarPanels()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (openFilterOptionsId !== null) {
        setOpenFilterOptionsId(null)
        setFilterValueQuery('')
        return
      }
      closeToolbarPanels()
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [fieldPanelOpen, filterPanelOpen, openFilterOptionsId, sortPanelOpen])

  useEffect(() => {
    if (!openSavedViewMenuId) return
    const closeSavedViewMenu = (event: PointerEvent) => {
      if (savedViewBarRef.current && event.composedPath().includes(savedViewBarRef.current)) return
      setOpenSavedViewMenuId('')
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenSavedViewMenuId('')
    }
    document.addEventListener('pointerdown', closeSavedViewMenu)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', closeSavedViewMenu)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [openSavedViewMenuId])

  useEffect(() => {
    localStorage.setItem(SKU_SUMMARY_STORAGE_KEY, JSON.stringify(summaryModes))
  }, [summaryModes])
  useEffect(() => {
    try {
      localStorage.setItem(SKU_SAVED_VIEWS_STORAGE_KEY, JSON.stringify(savedViews))
      localStorage.removeItem(SKU_LEGACY_SAVED_VIEWS_STORAGE_KEY)
    } catch {
      // 浏览器禁用本地存储时仍保持当前会话可用。
    }
  }, [savedViews])
  useEffect(() => {
    if (initialSavedViewAppliedRef.current) return
    initialSavedViewAppliedRef.current = true
    const initialView = savedViews.find((savedView) => savedView.id === activeSavedViewId)
    if (!initialView) {
      if (activeSavedViewId !== 'default') setActiveSavedViewId('default')
      return
    }
    setSearch(initialView.config.search)
    setTagId(initialView.config.tagId)
    setCostFilter(initialView.config.costFilter)
    setFilterConditions(initialView.config.filters.map((filter) => ({
      ...filter,
      id: nextFilterId.current++,
    })))
    setSortRule(initialView.config.sort)
    setHiddenColumns(new Set(initialView.config.hiddenColumns))
    setColumnWidths({ ...initialView.config.columnWidths })
    setColumnOrder([...initialView.config.columnOrder])
  }, [activeSavedViewId, savedViews])
  useEffect(() => {
    localStorage.setItem(SKU_ACTIVE_SAVED_VIEW_STORAGE_KEY, activeSavedViewId)
  }, [activeSavedViewId])
  useEffect(() => {
    const toolbar = toolbarRef.current
    if (!toolbar) return
    const syncToolbarGeometry = () => {
      setStickyToolbarHeight(Math.ceil(toolbar.getBoundingClientRect().height))
      const computedTop = Number.parseFloat(window.getComputedStyle(toolbar).top)
      setStickyToolbarTop(Number.isFinite(computedTop) ? computedTop : 0)
    }
    syncToolbarGeometry()
    const resizeObserver = new ResizeObserver(syncToolbarGeometry)
    resizeObserver.observe(toolbar)
    window.addEventListener('resize', syncToolbarGeometry)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', syncToolbarGeometry)
    }
  }, [])
  useEffect(() => {
    const syncWindowScroll = () => {
      windowScrollFrameRef.current = null
      const table = tableScrollRef.current
      if (!table) return
      const tableRect = table.getBoundingClientRect()
      const tableTop = tableRect.top + window.scrollY
      setTableScrollTop(Math.max(0, window.scrollY - tableTop))
      setViewportHeight(window.innerHeight)
      const left = Math.max(0, tableRect.left)
      const right = Math.min(window.innerWidth, tableRect.right)
      const nextSummaryDock = {
        visible: tableRect.top < window.innerHeight && tableRect.bottom > window.innerHeight,
        left,
        width: Math.max(0, right - left),
      }
      setSummaryDock((current) =>
        current.visible === nextSummaryDock.visible
        && current.left === nextSummaryDock.left
        && current.width === nextSummaryDock.width
          ? current
          : nextSummaryDock,
      )
    }
    const scheduleWindowScrollSync = () => {
      if (windowScrollFrameRef.current !== null) return
      windowScrollFrameRef.current = window.requestAnimationFrame(syncWindowScroll)
    }
    scheduleWindowScrollSync()
    window.addEventListener('scroll', scheduleWindowScrollSync, { passive: true })
    window.addEventListener('resize', scheduleWindowScrollSync)
    const resizeObserver = new ResizeObserver(scheduleWindowScrollSync)
    resizeObserver.observe(document.body)
    return () => {
      window.removeEventListener('scroll', scheduleWindowScrollSync)
      window.removeEventListener('resize', scheduleWindowScrollSync)
      resizeObserver.disconnect()
      if (windowScrollFrameRef.current !== null) {
        window.cancelAnimationFrame(windowScrollFrameRef.current)
      }
    }
  }, [])

  const skus = useMemo(
    () => data?.products.filter((product) => product.level === 'sku') || [],
    [data],
  )
  const erpInventoryByProductCode = useMemo(() => new Map(
    (erpInventory?.records || []).map((record) => [record.productCode.trim(), record]),
  ), [erpInventory])
  const records = useMemo(() => productMasterRecordMap(productMaster, JD_STORE_ID), [productMaster])
  const costDefaults = useMemo(() => productMasterCostDefaults(productMaster, JD_STORE_ID), [productMaster])
  const tagNamesById = useMemo(
    () => new Map((productMaster?.tags || []).map((tag) => [tag.id, tag.name])),
    [productMaster],
  )
  const filterValueOptions = useMemo<Partial<Record<SkuColumnId, string[]>>>(() => {
    const collator = new Intl.Collator('zh-CN', { numeric: true, sensitivity: 'base' })
    const uniqueValues = (values: Array<string | null | undefined>) =>
      Array.from(new Set(values.map((value) => value?.trim() || '').filter(Boolean))).sort(collator.compare)
    const companyRecords = internalProductInfo?.records || []
    return {
      ip: uniqueValues(companyRecords.map((record) => record.ip)),
      cupType: uniqueValues(companyRecords.map((record) => record.cupType)),
      brand: uniqueValues(companyRecords.map((record) => record.brand)),
      material: uniqueValues(companyRecords.map((record) => record.material)),
      audience: uniqueValues(companyRecords.map((record) => record.audience)),
      accessory: ['是', '否'],
      listed: ['是', '否'],
      inventoryCostMode: ['总代库存', '特价库存'],
      shippingProvider: ['京东入仓', '京东快递', '圆通快递'],
      tags: (productMaster?.tags || []).map((tag) => tag.name),
    }
  }, [internalProductInfo, productMaster])
  const internalRecordsByCode = useMemo(() => {
    const index = new Map<string, InternalProductInfoRecord[]>()
    for (const record of internalProductInfo?.records || []) {
      if (!record.productCode) continue
      const matches = index.get(record.productCode) || []
      matches.push(record)
      index.set(record.productCode, matches)
    }
    return index
  }, [internalProductInfo])
  const internalCostsBySku = useMemo(() => new Map(
    skus.map((sku) => [
      sku.id,
      internalProductCostOn(
        internalRecordsByCode.get(sku.merchantSku.trim()) || [],
        records.get(sku.id),
        selectedDate,
      ),
    ]),
  ), [internalRecordsByCode, records, selectedDate, skus])
  const currentInventoryCostModesBySku = useMemo(() => {
    const today = shanghaiToday()
    return new Map(skus.map((sku) => {
      const record = records.get(sku.id)
      const baseCost = internalProductCost(internalRecordsByCode.get(sku.merchantSku.trim()) || [])
      return [sku.id, inventoryCostModeOn(record, today, baseCost.inventoryCostMode)]
    }))
  }, [internalRecordsByCode, records, skus])
  const accessoryBySku = useMemo(() => new Map(
    skus.map((sku) => [
      sku.id,
      accessoryFor(
        sku,
        records.get(sku.id),
        internalRecordsByCode.get(sku.merchantSku.trim()) || [],
      ),
    ]),
  ), [internalRecordsByCode, records, skus])
  const listedBySku = useMemo(() => new Map(
    skus.map((sku) => [sku.id, listedFor(sku, records.get(sku.id))]),
  ), [records, skus])
  const isJdWarehouse = (skuId: string) => skuTagAssignments(segmentIndex, skuId, 'jd-warehouse').length > 0
  const costRecordedCount = useMemo(
    () => skus.filter((sku) => internalCostsBySku.get(sku.id)?.productCost !== null).length,
    [internalCostsBySku, skus],
  )
  const jdWarehouseCount = useMemo(
    () => skus.filter((sku) =>
      effectiveFulfillmentMode(records.get(sku.id), isJdWarehouse(sku.id)) === 'jd-warehouse',
    ).length,
    [records, skus, segmentIndex],
  )
  const listedCount = useMemo(
    () => skus.filter((sku) => listedBySku.get(sku.id)).length,
    [listedBySku, skus],
  )
  const erpLinkedSkuCount = useMemo(
    () => skus.filter((sku) => erpInventoryByProductCode.has(sku.merchantSku.trim())).length,
    [erpInventoryByProductCode, skus],
  )
  const activeFilterConditions = useMemo(
    () => filterConditions.filter(filterConditionIsActive),
    [filterConditions],
  )
  const orderedColumns = useMemo(() => {
    const seen = new Set<SkuColumnId>()
    const requestedIds = columnOrder.filter((columnId) => {
      if (!SKU_COLUMN_IDS.has(columnId) || seen.has(columnId)) return false
      seen.add(columnId)
      return true
    })
    const requestedColumns = [...requestedIds, ...SKU_COLUMNS.map((column) => column.id).filter((columnId) => !seen.has(columnId))]
      .map((columnId) => SKU_COLUMNS.find((column) => column.id === columnId))
      .filter((column): column is SkuColumnDefinition => Boolean(column))
    return [
      ...requestedColumns.filter((column) => column.id === 'rowNumber'),
      ...requestedColumns.filter((column) => column.id !== 'rowNumber'),
    ]
  }, [columnOrder])
  const currentViewConfig = useMemo<SkuViewConfig>(() => ({
    search,
    tagId,
    costFilter,
    filters: activeFilterConditions.map(({ field, operator, value }) => ({ field, operator, value })),
    sort: sortRule,
    hiddenColumns: SKU_COLUMNS.filter((column) => hiddenColumns.has(column.id)).map((column) => column.id),
    columnWidths: validSkuColumnWidths(columnWidths),
    columnOrder,
  }), [activeFilterConditions, columnOrder, columnWidths, costFilter, hiddenColumns, search, sortRule, tagId])
  const presetViews = useMemo(() => presetSkuViews(defaultViewConfig), [defaultViewConfig])
  const allViews = useMemo(() => [...presetViews, ...savedViews], [presetViews, savedViews])
  const activeSavedView = allViews.find((savedView) => savedView.id === activeSavedViewId)
  const openSavedViewMenu = savedViews.find((savedView) => savedView.id === openSavedViewMenuId)
  const activeViewConfig = activeSavedView?.config || defaultViewConfig
  const viewDirty = JSON.stringify(currentViewConfig) !== JSON.stringify(activeViewConfig)

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const matches = skus.filter((sku) => {
      const record = records.get(sku.id)
      const companyRecords = internalRecordsByCode.get(sku.merchantSku.trim()) || []
      const erpRecord = erpInventoryByProductCode.get(sku.merchantSku.trim())
      const isAccessory = accessoryBySku.get(sku.id) ?? false
      const isListed = listedBySku.get(sku.id) ?? false
      const internalCost = internalCostsBySku.get(sku.id) || internalProductCost(companyRecords)
      const currentInventoryCostMode = currentInventoryCostModesBySku.get(sku.id) || internalCost.inventoryCostMode
      const hasCost = internalCost.productCost !== null
      const costVersion = costVersionOn(record, selectedDate)
      const shippingProvider = shippingProviderFor(
        record,
        isJdWarehouse(sku.id),
        selectedDate,
        costDefaults,
        isAccessory,
        internalCost.isBrandSpecial,
      )
      const cost = resolvedProductCost(
        record,
        selectedDate,
        costDefaults,
        shippingProvider,
        isAccessory || internalCost.isBrandSpecial,
      )
      const rebateRate = rebateRateFor(isAccessory, internalCost.isBrandSpecial, internalCost.productCost)
      const estimate = activeFilterConditions.length
        ? estimatedProfitForSku(sku, record, [selectedDate], costDefaults, companyRecords, shippingProvider)
        : null
      const rebateAmount = rebateAmountFor(
        internalCost.productCost,
        rebateRate,
        estimate?.metrics.soldItemCount || 0,
      )
      const profitWithRebate = profitWithRebateFor(
        estimate?.profit,
        rebateAmount,
      )
      const profitWithRebateMargin = profitWithRebate !== null && (estimate?.metrics.gmv || 0) > 0
        ? profitWithRebate / (estimate?.metrics.gmv || 1) * 100
        : null
      const valuesForField = (field: SkuColumnId): Array<string | number | null | undefined> => {
        if (field === 'image') return [sku.image]
        if (field === 'productName') return [sku.name]
        if (field === 'spu') return [sku.spuId]
        if (field === 'jdSku') return [sku.id]
        if (field === 'merchantSku') return [sku.merchantSku]
        if (field === 'erpAvailable') return [erpRecord?.available]
        if (field === 'companyProductName') return companyRecords.map((company) => company.productName)
        if (field === 'barcode') return companyRecords.map((company) => company.barcode)
        if (field === 'generalAgentPrice') return companyRecords.map((company) => company.generalAgentPrice)
        if (field === 'brandSpecialPrice') return [internalCost.brandSpecialPrice]
        if (field === 'productCost') return [internalCost.productCost]
        if (field === 'inventoryCostMode') return [inventoryCostModeLabel(currentInventoryCostMode)]
        if (field === 'inventoryCostHistory') return [(record?.inventoryCostOperations || []).length ? `${(record?.inventoryCostOperations || []).length}条` : null]
        if (field === 'rebateRate') return [rebateRate]
        if (field === 'rebateAmount') return [rebateAmount]
        if (field === 'provincialAgentPrice') return companyRecords.map((company) => company.provincialAgentPrice)
        if (field === 'ecommercePrice') return companyRecords.map((company) => company.ecommercePrice)
        if (field === 'ip') return companyRecords.map((company) => company.ip)
        if (field === 'capacity') return companyRecords.map((company) => company.capacity)
        if (field === 'cupType') return companyRecords.map((company) => company.cupType)
        if (field === 'brand') return companyRecords.map((company) => company.brand)
        if (field === 'material') return companyRecords.map((company) => company.material)
        if (field === 'audience') return companyRecords.map((company) => company.audience)
        if (field === 'recordDate') return companyRecords.map((company) => company.recordDate)
        if (field === 'accessory') return [isAccessory ? '是' : '否']
        if (field === 'listed') return [isListed ? '是' : '否']
        if (field === 'packagingCost') return [cost.packagingCost]
        if (field === 'shippingProvider') return [shippingLabel(cost.shippingProvider)]
        if (field === 'shippingFee') return [cost.shippingFee]
        if (field === 'giftCost') return [cost.giftCost]
        if (field === 'shippingInsuranceFee') return [cost.shippingInsuranceFee]
        if (field === 'platformFeeRate') return [cost.platformFeeRate]
        if (field === 'taxRate') return [cost.taxRate]
        if (field === 'tags') return (record?.tagIds || []).map((id) => tagNamesById.get(id) || id)
        if (field === 'effectiveDate') return [costVersion?.effectiveDate]
        if (field === 'gmv') return [estimate?.metrics.gmv]
        if (field === 'soldItemCount') return [estimate?.metrics.soldItemCount]
        if (field === 'profit') return [estimate?.profit]
        if (field === 'profitWithRebate') return [profitWithRebate]
        if (field === 'profitWithRebateMargin') return [profitWithRebateMargin]
        return [estimate?.margin === null || estimate?.margin === undefined ? null : estimate.margin * 100]
      }
      return (!needle || [
        sku.name,
        sku.sourceName,
        sku.spuId,
        sku.id,
        sku.jdSkuId,
        sku.merchantSku,
        erpRecord?.productName,
        erpRecord?.barcode,
        ...companyRecords.flatMap((company) => [
          company.productName,
          company.barcode,
          company.generalAgentPrice,
          company.brandSpecialInitialPrice,
          company.specialPrice0225,
          company.specialPrice0305,
          company.provincialAgentPrice,
          company.ecommercePrice,
          company.ip,
          company.capacity,
          company.cupType,
          company.brand,
          company.material,
          company.audience,
          company.recordDate,
        ]),
      ]
        .some((value) => String(value ?? '').toLowerCase().includes(needle)))
        && (!tagId || record?.tagIds.includes(tagId))
        && (costFilter === 'all' || (costFilter === 'recorded' ? hasCost : !hasCost))
        && activeFilterConditions.every((condition) =>
          matchesSkuFilter(valuesForField(condition.field), condition),
        )
    })
    if (!sortRule) return matches

    const sortEntries = matches.map((sku, originalIndex) => {
      const record = records.get(sku.id)
      const companyRecords = internalRecordsByCode.get(sku.merchantSku.trim()) || []
      const erpRecord = erpInventoryByProductCode.get(sku.merchantSku.trim())
      const isAccessory = accessoryBySku.get(sku.id) ?? false
      const isListed = listedBySku.get(sku.id) ?? false
      const inheritedJdWarehouse = isJdWarehouse(sku.id)
      const internalCost = internalCostsBySku.get(sku.id) || internalProductCost(companyRecords)
      const currentInventoryCostMode = currentInventoryCostModesBySku.get(sku.id) || internalCost.inventoryCostMode
      const shippingProvider = shippingProviderFor(
        record,
        inheritedJdWarehouse,
        selectedDate,
        costDefaults,
        isAccessory,
        internalCost.isBrandSpecial,
      )
      const cost = resolvedProductCost(
        record,
        selectedDate,
        costDefaults,
        shippingProvider,
        isAccessory || internalCost.isBrandSpecial,
      )
      const rebateRate = rebateRateFor(isAccessory, internalCost.isBrandSpecial, internalCost.productCost)
      const costVersion = costVersionOn(record, selectedDate)
      const estimate = ['gmv', 'soldItemCount', 'profit', 'rebateAmount', 'profitWithRebate', 'profitWithRebateMargin', 'margin'].includes(sortRule.field)
        ? estimatedProfitForSku(sku, record, [selectedDate], costDefaults, companyRecords, shippingProvider)
        : null
      const rebateAmount = rebateAmountFor(
        internalCost.productCost,
        rebateRate,
        estimate?.metrics.soldItemCount || 0,
      )
      const profitWithRebate = profitWithRebateFor(
        estimate?.profit,
        rebateAmount,
      )
      const profitWithRebateMargin = profitWithRebate !== null && (estimate?.metrics.gmv || 0) > 0
        ? profitWithRebate / (estimate?.metrics.gmv || 1) * 100
        : null
      const firstCompanyValue = (
        read: (company: InternalProductInfoRecord) => string | number | null | undefined,
      ) => companyRecords.map(read).find((value) => value !== null && value !== undefined && String(value).trim() !== '')
      let value: string | number | null | undefined
      if (sortRule.field === 'productName') value = sku.name
      else if (sortRule.field === 'spu') value = sku.spuId
      else if (sortRule.field === 'jdSku') value = sku.id
      else if (sortRule.field === 'merchantSku') value = sku.merchantSku
      else if (sortRule.field === 'erpAvailable') value = erpRecord?.available
      else if (sortRule.field === 'companyProductName') value = firstCompanyValue((company) => company.productName)
      else if (sortRule.field === 'barcode') value = firstCompanyValue((company) => company.barcode)
      else if (sortRule.field === 'generalAgentPrice') value = firstCompanyValue((company) => company.generalAgentPrice)
      else if (sortRule.field === 'brandSpecialPrice') value = internalCost.brandSpecialPrice
      else if (sortRule.field === 'productCost') value = internalCost.productCost
      else if (sortRule.field === 'inventoryCostMode') value = inventoryCostModeLabel(currentInventoryCostMode)
      else if (sortRule.field === 'inventoryCostHistory') value = (record?.inventoryCostOperations || []).length
      else if (sortRule.field === 'rebateRate') value = rebateRate
      else if (sortRule.field === 'rebateAmount') value = rebateAmount
      else if (sortRule.field === 'provincialAgentPrice') value = firstCompanyValue((company) => company.provincialAgentPrice)
      else if (sortRule.field === 'ecommercePrice') value = firstCompanyValue((company) => company.ecommercePrice)
      else if (sortRule.field === 'ip') value = firstCompanyValue((company) => company.ip)
      else if (sortRule.field === 'capacity') value = firstCompanyValue((company) => company.capacity)
      else if (sortRule.field === 'cupType') value = firstCompanyValue((company) => company.cupType)
      else if (sortRule.field === 'brand') value = firstCompanyValue((company) => company.brand)
      else if (sortRule.field === 'material') value = firstCompanyValue((company) => company.material)
      else if (sortRule.field === 'audience') value = firstCompanyValue((company) => company.audience)
      else if (sortRule.field === 'recordDate') value = firstCompanyValue((company) => company.recordDate)
      else if (sortRule.field === 'accessory') value = isAccessory ? '是' : '否'
      else if (sortRule.field === 'listed') value = isListed ? '是' : '否'
      else if (sortRule.field === 'packagingCost') value = cost.packagingCost
      else if (sortRule.field === 'shippingProvider') value = shippingLabel(cost.shippingProvider)
      else if (sortRule.field === 'shippingFee') value = cost.shippingFee
      else if (sortRule.field === 'giftCost') value = cost.giftCost
      else if (sortRule.field === 'shippingInsuranceFee') value = cost.shippingInsuranceFee
      else if (sortRule.field === 'platformFeeRate') value = cost.platformFeeRate
      else if (sortRule.field === 'taxRate') value = cost.taxRate
      else if (sortRule.field === 'tags') value = (record?.tagIds || []).map((id) => tagNamesById.get(id) || id).join(' / ')
      else if (sortRule.field === 'effectiveDate') value = costVersion?.effectiveDate
      else if (sortRule.field === 'gmv') value = estimate?.metrics.gmv
      else if (sortRule.field === 'soldItemCount') value = estimate?.metrics.soldItemCount
      else if (sortRule.field === 'profit') value = estimate?.profit
      else if (sortRule.field === 'profitWithRebate') value = profitWithRebate
      else if (sortRule.field === 'profitWithRebateMargin') value = profitWithRebateMargin
      else value = estimate?.margin === null || estimate?.margin === undefined ? null : estimate.margin * 100
      return { sku, originalIndex, value }
    })
    const collator = new Intl.Collator('zh-CN', { numeric: true, sensitivity: 'base' })
    sortEntries.sort((left, right) => {
      const leftEmpty = left.value === null || left.value === undefined || left.value === ''
      const rightEmpty = right.value === null || right.value === undefined || right.value === ''
      if (leftEmpty && rightEmpty) return left.originalIndex - right.originalIndex
      if (leftEmpty) return 1
      if (rightEmpty) return -1
      const comparison = typeof left.value === 'number' && typeof right.value === 'number'
        ? left.value - right.value
        : collator.compare(String(left.value), String(right.value))
      return comparison === 0
        ? left.originalIndex - right.originalIndex
        : sortRule.direction === 'asc' ? comparison : -comparison
    })
    return sortEntries.map((entry) => entry.sku)
  }, [accessoryBySku, activeFilterConditions, costDefaults, costFilter, currentInventoryCostModesBySku, erpInventoryByProductCode, internalCostsBySku, internalRecordsByCode, listedBySku, records, search, selectedDate, skus, sortRule, tagId, segmentIndex, tagNamesById])

  const summarySums = useMemo(() => {
    const sumFields = SKU_COLUMNS
      .map((column) => column.id)
      .filter((field) => summaryModes[field] === 'sum' && SUMMABLE_SKU_FIELDS.has(field))
    const totals: Partial<Record<SkuColumnId, number>> = Object.fromEntries(
      sumFields.map((field) => [field, 0]),
    )
    if (!sumFields.length) return totals

    for (const sku of filtered) {
      const record = records.get(sku.id)
      const companyRecords = internalRecordsByCode.get(sku.merchantSku.trim()) || []
      const isAccessory = accessoryBySku.get(sku.id) ?? false
      const internalCost = internalCostsBySku.get(sku.id) || internalProductCost(companyRecords)
      const shippingProvider = shippingProviderFor(
        record,
        isJdWarehouse(sku.id),
        selectedDate,
        costDefaults,
        isAccessory,
        internalCost.isBrandSpecial,
      )
      const cost = resolvedProductCost(
        record,
        selectedDate,
        costDefaults,
        shippingProvider,
        isAccessory || internalCost.isBrandSpecial,
      )
      const rebateRate = rebateRateFor(isAccessory, internalCost.isBrandSpecial, internalCost.productCost)
      const needsEstimate = ['gmv', 'soldItemCount', 'profit', 'rebateAmount', 'profitWithRebate', 'profitWithRebateMargin', 'margin'].some((field) =>
        sumFields.includes(field as SkuColumnId),
      )
      const estimate = needsEstimate
        ? estimatedProfitForSku(sku, record, [selectedDate], costDefaults, companyRecords, shippingProvider)
        : null
      const rebateAmount = rebateAmountFor(
        internalCost.productCost,
        rebateRate,
        estimate?.metrics.soldItemCount || 0,
      )
      const profitWithRebate = profitWithRebateFor(
        estimate?.profit,
        rebateAmount,
      )
      const profitWithRebateMargin = profitWithRebate !== null && (estimate?.metrics.gmv || 0) > 0
        ? profitWithRebate / (estimate?.metrics.gmv || 1) * 100
        : null

      for (const field of sumFields) {
        let values: Array<number | null | undefined>
        if (field === 'generalAgentPrice') values = companyRecords.map((company) => company.generalAgentPrice)
        else if (field === 'brandSpecialPrice') values = [internalCost.brandSpecialPrice]
        else if (field === 'productCost') values = [internalCost.productCost]
        else if (field === 'rebateRate') values = [rebateRate]
        else if (field === 'rebateAmount') values = [rebateAmount]
        else if (field === 'provincialAgentPrice') values = companyRecords.map((company) => company.provincialAgentPrice)
        else if (field === 'ecommercePrice') values = companyRecords.map((company) => company.ecommercePrice)
        else if (field === 'capacity') values = companyRecords.map((company) => company.capacity)
        else if (field === 'packagingCost') values = [cost.packagingCost]
        else if (field === 'shippingFee') values = [cost.shippingFee]
        else if (field === 'giftCost') values = [cost.giftCost]
        else if (field === 'shippingInsuranceFee') values = [cost.shippingInsuranceFee]
        else if (field === 'platformFeeRate') values = [cost.platformFeeRate]
        else if (field === 'taxRate') values = [cost.taxRate]
        else if (field === 'gmv') values = [estimate?.metrics.gmv]
        else if (field === 'soldItemCount') values = [estimate?.metrics.soldItemCount]
        else if (field === 'profit') values = [estimate?.profit]
        else if (field === 'profitWithRebate') values = [profitWithRebate]
        else if (field === 'profitWithRebateMargin') values = [profitWithRebateMargin]
        else values = [estimate?.margin === null || estimate?.margin === undefined ? null : estimate.margin * 100]

        for (const value of new Set(values)) {
          if (typeof value === 'number' && Number.isFinite(value)) {
            totals[field] = (totals[field] || 0) + value
          }
        }
      }
    }
    return totals
  }, [accessoryBySku, costDefaults, filtered, internalCostsBySku, internalRecordsByCode, records, selectedDate, segmentIndex, summaryModes])

  const virtualVisibleCount = Math.ceil(viewportHeight / SKU_ROW_HEIGHT)
  const virtualScrollStart = Math.min(
    Math.floor(tableScrollTop / SKU_ROW_HEIGHT),
    Math.max(0, filtered.length - virtualVisibleCount),
  )
  const virtualStart = Math.max(0, virtualScrollStart - SKU_VIRTUAL_OVERSCAN)
  const virtualEnd = Math.min(
    filtered.length,
    virtualScrollStart + virtualVisibleCount + SKU_VIRTUAL_OVERSCAN,
  )
  const virtualRows = filtered.slice(virtualStart, virtualEnd)
  const virtualTopHeight = virtualStart * SKU_ROW_HEIGHT
  const virtualBottomHeight = (filtered.length - virtualEnd) * SKU_ROW_HEIGHT
  const visibleColumns = orderedColumns.filter((column) => !hiddenColumns.has(column.id))
  const visibleColumnCount = visibleColumns.length
  const columnWidth = (column: SkuColumnDefinition) => columnWidths[column.id] || column.width
  const visibleTableWidth = Math.max(900, visibleColumns.reduce((total, column) => total + columnWidth(column), 0))
  const columnVisible = (columnId: SkuColumnId) => !hiddenColumns.has(columnId)
  const finishColumnDrag = () => {
    columnPointerDragRef.current = null
    columnDropTargetRef.current = null
    setDraggedColumnId('')
    setColumnDropTarget(null)
    document.body.classList.remove('is-dragging-column')
  }
  const scrollTableDuringColumnDrag = (clientX: number) => {
    const table = tableScrollRef.current
    if (!table) return
    const rect = table.getBoundingClientRect()
    const edge = Math.min(72, rect.width / 4)
    if (clientX < rect.left + edge) table.scrollLeft = Math.max(0, table.scrollLeft - 28)
    else if (clientX > rect.right - edge) table.scrollLeft = Math.min(table.scrollWidth - table.clientWidth, table.scrollLeft + 28)
  }
  const reorderColumn = (sourceId: SkuColumnId, targetId: SkuColumnId, position: 'before' | 'after') => {
    if (sourceId === 'rowNumber' || sourceId === targetId) return
    const nextIds = orderedColumns.map((column) => column.id).filter((columnId) => columnId !== sourceId)
    const targetIndex = nextIds.indexOf(targetId)
    const safePosition = targetId === 'rowNumber' ? 'after' : position
    nextIds.splice(Math.max(0, targetIndex + (safePosition === 'after' ? 1 : 0)), 0, sourceId)
    setColumnOrder([
      ...nextIds.filter((columnId) => columnId === 'rowNumber'),
      ...nextIds.filter((columnId) => columnId !== 'rowNumber'),
    ])
  }
  const handleColumnPointerDown = (
    event: ReactPointerEvent<HTMLTableCellElement>,
    column: SkuColumnDefinition,
  ) => {
    if (event.button !== 0 || column.id === 'rowNumber' || (event.target as Element).closest('.sku-column-resizer')) return
    event.preventDefault()
    columnPointerDragRef.current = { id: column.id, startX: event.clientX, startY: event.clientY, active: false }
    columnDropTargetRef.current = null
    setColumnDropTarget(null)
  }
  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const drag = columnPointerDragRef.current
      if (!drag) return
      if (!drag.active) {
        if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 5) return
        drag.active = true
        setDraggedColumnId(drag.id)
        document.body.classList.add('is-dragging-column')
      }
      event.preventDefault()
      scrollTableDuringColumnDrag(event.clientX)
      const workspace = workspaceRef.current
      if (!workspace) return
      const visibleHeaders = Array.from(workspace.querySelectorAll<HTMLTableCellElement>('.sku-master-sticky-header th[data-column-id]'))
        .filter((header) => {
          const rect = header.getBoundingClientRect()
          return rect.width > 0 && rect.height > 0
        })
      const pointedHeader = (document.elementFromPoint(event.clientX, event.clientY) as Element | null)
        ?.closest<HTMLTableCellElement>('.sku-master-sticky-header th[data-column-id]')
      const targetHeader = pointedHeader && workspace.contains(pointedHeader)
        ? pointedHeader
        : visibleHeaders.find((header) => event.clientX <= header.getBoundingClientRect().right) || visibleHeaders.at(-1)
      const targetId = targetHeader?.dataset.columnId as SkuColumnId | undefined
      if (!targetHeader || !targetId || targetId === drag.id) {
        columnDropTargetRef.current = null
        setColumnDropTarget(null)
        return
      }
      const rect = targetHeader.getBoundingClientRect()
      const requestedPosition: 'before' | 'after' = event.clientX < rect.left + rect.width / 2 ? 'before' : 'after'
      const nextTarget = { id: targetId, position: targetId === 'rowNumber' ? 'after' as const : requestedPosition }
      columnDropTargetRef.current = nextTarget
      setColumnDropTarget((current) => current?.id === nextTarget.id && current.position === nextTarget.position ? current : nextTarget)
    }
    const handlePointerUp = (event: PointerEvent) => {
      const drag = columnPointerDragRef.current
      const target = columnDropTargetRef.current
      if (drag?.active) {
        event.preventDefault()
        if (target) reorderColumn(drag.id, target.id, target.position)
      } else if (drag && HEADER_SORTABLE_SKU_FIELDS.has(drag.id)) {
        toggleHeaderSort(drag.id)
      }
      finishColumnDrag()
    }
    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerUp)
    document.addEventListener('pointercancel', handlePointerUp)
    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
      document.removeEventListener('pointercancel', handlePointerUp)
      document.body.classList.remove('is-dragging-column')
    }
  }, [orderedColumns])
  const renderSummaryCells = (surface: 'source' | 'docked') => visibleColumns.map((column) => {
    const mode = summaryModes[column.id] || 'none'
    const countLabel = filtered.length.toLocaleString('zh-CN')
    const sumLabel = formatSkuSummaryValue(column.id, summarySums[column.id] || 0)
    return (
      <td key={column.id} className={column.id === 'rowNumber' ? 'sku-master-rownumber-cell' : ''}>
        {column.id !== 'rowNumber' && (
          <select
            className="sku-summary-select"
            aria-label={`${column.label}底部统计`}
            tabIndex={surface === 'source' && summaryDock.visible ? -1 : 0}
            value={mode}
            onChange={(event) => {
              const nextMode = event.target.value as SkuSummaryMode
              setSummaryModes((current) => ({ ...current, [column.id]: nextMode }))
            }}
          >
            <option value="none">{mode === 'none' ? '' : '不展示'}</option>
            <option value="count">记录总数{mode === 'count' ? ` · ${countLabel}` : ''}</option>
            <option value="sum" disabled={!SUMMABLE_SKU_FIELDS.has(column.id)}>
              {mode === 'sum' ? sumLabel : '求和'}
            </option>
          </select>
        )}
      </td>
    )
  })

  const copySkuId = async (value: string, feedbackKey: string) => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      const input = document.createElement('textarea')
      input.value = value
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      input.remove()
    }
    setCopiedSkuId(feedbackKey)
    window.clearTimeout(copySkuFeedbackTimer.current)
    copySkuFeedbackTimer.current = window.setTimeout(() => setCopiedSkuId(''), 1500)
  }

  const setColumnWidth = (column: SkuColumnDefinition, width: number) => {
    const minimum = column.id === 'productName' ? 140 : MIN_SKU_COLUMN_WIDTH
    const nextWidth = Math.round(Math.min(MAX_SKU_COLUMN_WIDTH, Math.max(minimum, width)))
    setColumnWidths((current) => ({ ...current, [column.id]: nextWidth }))
  }

  const startColumnResize = (
    event: ReactPointerEvent<HTMLSpanElement>,
    column: SkuColumnDefinition,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    const startX = event.clientX
    const startWidth = columnWidth(column)
    const handlePointerMove = (pointerEvent: PointerEvent) => {
      setColumnWidth(column, startWidth + pointerEvent.clientX - startX)
    }
    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
  }

  const resizeColumnWithKeyboard = (
    event: ReactKeyboardEvent<HTMLSpanElement>,
    column: SkuColumnDefinition,
  ) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    setColumnWidth(column, columnWidth(column) + (event.key === 'ArrowRight' ? 12 : -12))
  }

  const resetColumnWidth = (columnId: SkuColumnId) => {
    setColumnWidths((current) => {
      const next = { ...current }
      delete next[columnId]
      return next
    })
  }

  const applyAutoColumnWidths = (mode: 'header' | 'content') => {
    const sourceTable = tableScrollRef.current?.querySelector('table')
    if (!sourceTable) return
    const clone = sourceTable.cloneNode(true) as HTMLTableElement
    clone.style.tableLayout = 'auto'
    clone.style.width = 'auto'
    clone.style.minWidth = '0'
    clone.querySelectorAll('colgroup').forEach((group) => group.remove())
    clone.querySelectorAll('tfoot, .sku-virtual-spacer').forEach((element) => element.remove())
    if (mode === 'header') clone.querySelectorAll('tbody').forEach((body) => body.remove())
    clone.querySelectorAll('th, td').forEach((cell) => {
      (cell as HTMLElement).style.width = 'auto'
    })
    const host = document.createElement('div')
    host.style.cssText = 'position:absolute;top:0;left:-100000px;visibility:hidden'
    host.appendChild(clone)
    document.body.appendChild(host)
    const headerCells = Array.from(clone.querySelectorAll('thead th')) as HTMLElement[]
    const measured = new Map<SkuColumnId, number>()
    visibleColumns.forEach((column, index) => {
      const cell = headerCells[index]
      if (column.id !== 'rowNumber' && cell) measured.set(column.id, cell.getBoundingClientRect().width)
    })
    host.remove()
    if (!measured.size) return
    setColumnWidths((current) => {
      const next = { ...current }
      measured.forEach((width, columnId) => {
        const minimum = columnId === 'productName' ? 140 : MIN_SKU_COLUMN_WIDTH
        next[columnId] = Math.round(Math.min(MAX_SKU_COLUMN_WIDTH, Math.max(minimum, width + 2)))
      })
      return next
    })
  }

  const toggleColumnVisibility = (column: SkuColumnDefinition) => {
    if (column.locked) return
    setHiddenColumns((current) => {
      const next = new Set(current)
      if (next.has(column.id)) next.delete(column.id)
      else next.add(column.id)
      return next
    })
  }

  const applySkuViewConfig = (config: SkuViewConfig) => {
    setSearch(config.search)
    setTagId(config.tagId)
    setCostFilter(config.costFilter)
    setFilterConditions(config.filters.map((filter) => ({
      ...filter,
      id: nextFilterId.current++,
    })))
    setSortRule(config.sort)
    setHiddenColumns(new Set(config.hiddenColumns))
    setColumnWidths({ ...config.columnWidths })
    setColumnOrder([...config.columnOrder])
    setFieldPanelOpen(false)
    setFilterPanelOpen(false)
    setOpenFilterOptionsId(null)
    setFilterValueQuery('')
    setSortPanelOpen(false)
    setCreateViewOpen(false)
    setOpenSavedViewMenuId('')
    setPendingRenameViewId('')
    setPendingDeleteViewId('')
    setViewMessage('')
  }

  const switchSavedView = (viewId: string) => {
    if (viewId === activeSavedViewId) return
    if (viewDirty) {
      setViewMessage('当前视图配置尚未保存，请先保存或还原。')
      return
    }
    const nextView = allViews.find((savedView) => savedView.id === viewId)
    applySkuViewConfig(nextView?.config || defaultViewConfig)
    setActiveSavedViewId(viewId)
  }

  const restoreActiveView = () => {
    applySkuViewConfig(activeViewConfig)
  }

  const saveActiveView = () => {
    if (isPresetSkuViewId(activeSavedViewId)) {
      setCreateViewOpen(true)
      setNewViewName('')
      setViewMessage('预置页面不可覆盖，可保存为新页面。')
      return
    }
    if (activeSavedViewId === 'default') {
      setDefaultViewConfig(currentViewConfig)
      localStorage.setItem(SKU_DEFAULT_VIEW_STORAGE_KEY, JSON.stringify(currentViewConfig))
      localStorage.setItem(SKU_COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(currentViewConfig.hiddenColumns))
      localStorage.setItem(SKU_COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(currentViewConfig.columnWidths))
      setViewMessage('已保存“全部数据”。')
      return
    }
    if (activeSavedView?.locked) {
      setViewMessage(`“${activeSavedView.name}”已锁定，请先解锁视图。`)
      return
    }
    setSavedViews((current) => current.map((savedView) =>
      savedView.id === activeSavedViewId
        ? { ...savedView, config: currentViewConfig }
        : savedView,
    ))
    setViewMessage(`已保存“${activeSavedView?.name || '当前页面'}”。`)
  }

  const createSavedView = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const name = newViewName.trim()
    if (!name) {
      setViewMessage('请输入页面名称。')
      return
    }
    if (allViews.some((savedView) => savedView.name === name)) {
      setViewMessage('页面名称已存在。')
      return
    }
    const id = `sku-view-${Date.now()}`
    setSavedViews((current) => [...current, { id, name, config: currentViewConfig, locked: false }])
    setActiveSavedViewId(id)
    setCreateViewOpen(false)
    setNewViewName('')
    setViewMessage(`已新建“${name}”。`)
  }

  const deleteSavedView = (savedView: SavedSkuView) => {
    if (savedView.locked) {
      setPendingDeleteViewId('')
      setViewMessage(`“${savedView.name}”已锁定，请先解锁视图。`)
      return
    }
    setSavedViews((current) => current.filter((item) => item.id !== savedView.id))
    if (activeSavedViewId === savedView.id) {
      applySkuViewConfig(defaultViewConfig)
      setActiveSavedViewId('default')
    }
    setPendingDeleteViewId('')
    setViewMessage(`已删除“${savedView.name}”。`)
  }

  const uniqueSavedViewName = (baseName: string) => {
    const existingNames = new Set(allViews.map((savedView) => savedView.name))
    if (!existingNames.has(baseName)) return baseName
    let sequence = 2
    while (existingNames.has(`${baseName} ${sequence}`)) sequence += 1
    return `${baseName} ${sequence}`
  }

  const renameSavedView = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const savedView = savedViews.find((item) => item.id === pendingRenameViewId)
    if (!savedView) return
    if (savedView.locked) {
      setPendingRenameViewId('')
      setViewMessage(`“${savedView.name}”已锁定，请先解锁视图。`)
      return
    }
    const name = renameViewName.trim()
    if (!name) {
      setViewMessage('请输入页面名称。')
      return
    }
    if (allViews.some((item) => item.id !== savedView.id && item.name === name)) {
      setViewMessage('页面名称已存在。')
      return
    }
    setSavedViews((current) => current.map((item) =>
      item.id === savedView.id ? { ...item, name } : item,
    ))
    setPendingRenameViewId('')
    setRenameViewName('')
    setViewMessage(`已重命名为“${name}”。`)
  }

  const duplicateSavedView = (savedView: SavedSkuView) => {
    if (viewDirty) {
      setOpenSavedViewMenuId('')
      setViewMessage('当前视图配置尚未保存，请先保存或还原。')
      return
    }
    const name = uniqueSavedViewName(`${savedView.name} 副本`)
    const id = `sku-view-${Date.now()}`
    setSavedViews((current) => [...current, {
      id,
      name,
      config: {
        ...savedView.config,
        filters: savedView.config.filters.map((filter) => ({ ...filter })),
        sort: savedView.config.sort ? { ...savedView.config.sort } : null,
        hiddenColumns: [...savedView.config.hiddenColumns],
        columnWidths: { ...savedView.config.columnWidths },
        columnOrder: [...savedView.config.columnOrder],
      },
      locked: false,
    }])
    applySkuViewConfig(savedView.config)
    setActiveSavedViewId(id)
    setOpenSavedViewMenuId('')
    setViewMessage(`已复制为“${name}”。`)
  }

  const toggleSavedViewLock = (savedView: SavedSkuView) => {
    const locked = !savedView.locked
    setSavedViews((current) => current.map((item) =>
      item.id === savedView.id ? { ...item, locked } : item,
    ))
    setOpenSavedViewMenuId('')
    setViewMessage(`已${locked ? '锁定' : '解锁'}“${savedView.name}”。`)
  }

  const closeFilterValueOptions = () => {
    setOpenFilterOptionsId(null)
    setFilterValueQuery('')
  }

  const toggleFilterValueOptions = (id: number) => {
    setOpenFilterOptionsId((current) => {
      if (current === id) {
        setFilterValueQuery('')
        return null
      }
      setFilterValueQuery('')
      return id
    })
  }

  const toggleFilterPanel = () => {
    if (!filterPanelOpen && filterConditions.length === 0) {
      setFilterConditions([createFilterCondition(nextFilterId.current++)])
    }
    setFieldPanelOpen(false)
    setSortPanelOpen(false)
    if (filterPanelOpen) closeFilterValueOptions()
    setFilterPanelOpen((value) => !value)
  }

  const toggleSortPanel = () => {
    setFieldPanelOpen(false)
    setFilterPanelOpen(false)
    closeFilterValueOptions()
    setSortPanelOpen((value) => !value)
  }

  const toggleHeaderSort = (field: SkuColumnId) => {
    if (!HEADER_SORTABLE_SKU_FIELDS.has(field)) return
    setSortPanelOpen(false)
    setFieldPanelOpen(false)
    setFilterPanelOpen(false)
    closeFilterValueOptions()
    setSortRule((current) => {
      if (!current || current.field !== field) return { field, direction: 'desc' }
      return { field, direction: current.direction === 'desc' ? 'asc' : 'desc' }
    })
  }

  const updateFilterCondition = (id: number, patch: Partial<SkuFilterCondition>) => {
    setFilterConditions((current) => current.map((condition) =>
      condition.id === id ? { ...condition, ...patch } : condition,
    ))
  }

  const changeFilterField = (id: number, field: SkuColumnId) => {
    closeFilterValueOptions()
    updateFilterCondition(id, {
      field,
      operator: defaultOperatorForColumn(field),
      value: '',
    })
  }

  const addFilterCondition = () => {
    setFilterConditions((current) => [...current, createFilterCondition(nextFilterId.current++)])
  }

  const removeFilterCondition = (id: number) => {
    if (openFilterOptionsId === id) closeFilterValueOptions()
    setFilterConditions((current) => current.filter((condition) => condition.id !== id))
  }

  const clearFilterConditions = () => {
    closeFilterValueOptions()
    setFilterConditions([])
  }

  const saveRows = async (rows: BatchRow[]) => {
    const response = await authFetch('/api/product-master/records', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId: JD_STORE_ID, records: rows }),
    })
    const payload = await response.json() as { error?: string }
    if (!response.ok) throw new Error(payload.error || '保存失败')
    await Promise.all([reloadProductMaster(), reloadSegments()])
  }

  const updateDraft = (sku: ProductRecord, patch: Partial<RowDraft>) => {
    setDrafts((current) => {
      const next = new Map(current)
      const record = records.get(sku.id)
      const original = draftFor(
        sku,
        record,
        isJdWarehouse(sku.id),
        selectedDate,
        costDefaults,
        accessoryBySku.get(sku.id) ?? false,
        internalCostsBySku.get(sku.id)?.isBrandSpecial ?? false,
        listedBySku.get(sku.id) ?? false,
      )
      const updated = { ...(next.get(sku.id) || original), ...patch }
      if (draftsMatch(updated, original)) next.delete(sku.id)
      else next.set(sku.id, updated)
      return next
    })
    setError('')
  }

  const toggleEditLock = async () => {
    if (!editUnlocked) {
      setEditUnlocked(true)
      setError('')
      return
    }
    if (!drafts.size) {
      setEditUnlocked(false)
      return
    }
    setSaving(true)
    setError('')
    try {
      const rows = Array.from(drafts.values(), batchRowForDraft)
      await saveRows(rows)
      setDrafts(new Map())
      setEditUnlocked(false)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setSaving(false)
    }
  }

  if (!data || !productMaster) return null
  return (
    <div className="page-stack product-master-page">
      <div className="page-title-row">
        <div>
          <h1>京东SKU资料</h1>
          <p>按京东SKU维护该店铺的快递、费用覆盖和长期商品标签。</p>
        </div>
      </div>

      <div className="product-master-cost-note">
        <strong>字段与编辑边界</strong>
        <span>表格完整展示公司产品字段、京东店铺字段和经营指标，显示哪些字段由当前用户自定义。解锁后只能修改京东店铺的上架、履约、费用和标签；图片、配件、品牌特价与库存成本口径请到公司产品库维护。ERP库存按“商家SKU＝货品编号”关联，展示网店管家ERP的可订购数量，来自全渠道自有仓快照{erpInventory ? `（数据截至 ${erpInventory.source.exportedAt.replace('T', ' ').slice(0, 19)}）` : '（尚未导入）'}，只读展示；同一ERP货品关联多个京东SKU时会重复显示，底部不提供库存求和。</span>
      </div>
        <section className="product-master-summary" aria-label="商品资料概览">
        <div><span>表内上架SKU</span><strong>{listedCount.toLocaleString('zh-CN')}</strong></div>
        <div><span>ERP库存已关联</span><strong>{erpLinkedSkuCount.toLocaleString('zh-CN')}</strong></div>
        <div><span>产品成本已匹配</span><strong>{costRecordedCount.toLocaleString('zh-CN')}</strong></div>
        <div><span>京东入仓SKU</span><strong>{jdWarehouseCount.toLocaleString('zh-CN')}</strong></div>
        <div><span>可算毛利率</span><strong>{skus.length ? percent(costRecordedCount / skus.length * 100) : '0.00%'}</strong></div>
        </section>

        <div ref={workspaceRef} className="sku-master-workspace">
        <section ref={toolbarRef} className="product-master-toolbar sku-master-sticky-toolbar">
        <section ref={savedViewBarRef} className="product-master-saved-view-bar" aria-label="SKU页面管理">
          <div className="product-master-saved-view-tabs" role="tablist" aria-label="已保存页面">
            <button
              type="button"
              role="tab"
              aria-selected={activeSavedViewId === 'default'}
              className={activeSavedViewId === 'default' ? 'is-active' : ''}
              onClick={() => switchSavedView('default')}
            >
              <Table2 aria-hidden="true" />
              全部商品
            </button>
            {presetViews.map((presetView) => (
              <button
                key={presetView.id}
                type="button"
                role="tab"
                aria-selected={activeSavedViewId === presetView.id}
                className={activeSavedViewId === presetView.id ? 'is-active' : ''}
                title="预置页面：筛选与排序固定，调整后可另存为新页面"
                onClick={() => switchSavedView(presetView.id)}
              >
                <Table2 aria-hidden="true" />
                {presetView.name}
              </button>
            ))}
            {savedViews.map((savedView) => (
              <span
                key={savedView.id}
                className={`product-master-saved-view-tab${activeSavedViewId === savedView.id ? ' is-active' : ''}`}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSavedViewId === savedView.id}
                  onClick={() => switchSavedView(savedView.id)}
                >
                  <Table2 aria-hidden="true" />
                  {savedView.name}
                  {savedView.locked && <Lock className="product-master-view-lock" aria-label="已锁定" />}
                </button>
                <button
                  type="button"
                  className="product-master-saved-view-more"
                  aria-label={`页面设置 ${savedView.name}`}
                  aria-haspopup="menu"
                  aria-expanded={openSavedViewMenuId === savedView.id}
                  title="页面设置"
                  onClick={(event) => {
                    event.stopPropagation()
                    const buttonRect = event.currentTarget.getBoundingClientRect()
                    const barRect = savedViewBarRef.current?.getBoundingClientRect()
                    if (barRect) {
                      setSavedViewMenuPosition({
                        top: buttonRect.bottom - barRect.top + 2,
                        left: Math.max(8, Math.min(buttonRect.left - barRect.left, barRect.width - 180)),
                      })
                    }
                    setOpenSavedViewMenuId((current) => current === savedView.id ? '' : savedView.id)
                    setCreateViewOpen(false)
                    setPendingRenameViewId('')
                    setPendingDeleteViewId('')
                    setViewMessage('')
                  }}
                >
                  <MoreVertical aria-hidden="true" />
                </button>
              </span>
            ))}
            <button
              type="button"
              className="product-master-new-view"
              onClick={() => {
                setCreateViewOpen(true)
                setNewViewName('')
                setOpenSavedViewMenuId('')
                setPendingRenameViewId('')
                setPendingDeleteViewId('')
                setViewMessage('')
              }}
            >
              <Plus aria-hidden="true" />新建页面
            </button>
          </div>
          {openSavedViewMenu && (
            <div
              className="product-master-saved-view-menu"
              role="menu"
              aria-label={`${openSavedViewMenu.name}页面设置`}
              style={savedViewMenuPosition}
            >
              <button
                type="button"
                role="menuitem"
                disabled={openSavedViewMenu.locked}
                onClick={() => {
                  setOpenSavedViewMenuId('')
                  setPendingRenameViewId(openSavedViewMenu.id)
                  setRenameViewName(openSavedViewMenu.name)
                  setViewMessage('')
                }}
              >
                <Pencil aria-hidden="true" />重命名
              </button>
              <button type="button" role="menuitem" onClick={() => toggleSavedViewLock(openSavedViewMenu)}>
                {openSavedViewMenu.locked ? <LockOpen aria-hidden="true" /> : <Lock aria-hidden="true" />}
                {openSavedViewMenu.locked ? '解锁视图' : '锁定视图'}
              </button>
              <button type="button" role="menuitem" onClick={() => duplicateSavedView(openSavedViewMenu)}>
                <Copy aria-hidden="true" />复制视图
              </button>
              <div className="product-master-saved-view-menu-divider" />
              <button
                type="button"
                role="menuitem"
                className="is-danger"
                disabled={openSavedViewMenu.locked}
                onClick={() => {
                  setOpenSavedViewMenuId('')
                  setPendingDeleteViewId(openSavedViewMenu.id)
                  setCreateViewOpen(false)
                  setViewMessage('')
                }}
              >
                <Trash2 aria-hidden="true" />删除视图
              </button>
            </div>
          )}
          {createViewOpen && (
            <form className="product-master-create-view" onSubmit={createSavedView}>
              <input
                autoFocus
                aria-label="新页面名称"
                value={newViewName}
                maxLength={24}
                placeholder="输入页面名称"
                onChange={(event) => setNewViewName(event.target.value)}
              />
              <button type="submit">创建</button>
              <button type="button" onClick={() => {
                setCreateViewOpen(false)
                setNewViewName('')
                setViewMessage('')
              }}>取消</button>
            </form>
          )}
          {pendingRenameViewId && (
            <form className="product-master-create-view" onSubmit={renameSavedView}>
              <input
                autoFocus
                aria-label="重命名页面"
                value={renameViewName}
                maxLength={24}
                placeholder="输入页面名称"
                onChange={(event) => setRenameViewName(event.target.value)}
              />
              <button type="submit">确认</button>
              <button type="button" onClick={() => {
                setPendingRenameViewId('')
                setRenameViewName('')
                setViewMessage('')
              }}>取消</button>
            </form>
          )}
          {pendingDeleteViewId && (
            <div className="product-master-delete-view-confirm">
              <span>删除“{savedViews.find((savedView) => savedView.id === pendingDeleteViewId)?.name}”？</span>
              <button
                type="button"
                className="is-danger"
                onClick={() => {
                  const savedView = savedViews.find((item) => item.id === pendingDeleteViewId)
                  if (savedView) deleteSavedView(savedView)
                }}
              >
                确认删除
              </button>
              <button type="button" onClick={() => setPendingDeleteViewId('')}>取消</button>
            </div>
          )}
          {!createViewOpen && !pendingRenameViewId && !pendingDeleteViewId && viewDirty && (
            <div className="product-master-view-dirty">
              <span>视图配置已调整</span>
              <button type="button" onClick={restoreActiveView}>还原</button>
              <button type="button" className="is-primary" onClick={saveActiveView}>
                <Save aria-hidden="true" />{isPresetSkuViewId(activeSavedViewId) ? '保存为新页面' : '保存'}
              </button>
              {activeSavedViewId === 'default' && (
                <button type="button" onClick={() => { setCreateViewOpen(true); setNewViewName('') }}>
                  另存为新页面
                </button>
              )}
            </div>
          )}
          {viewMessage && <span className="product-master-view-message">{viewMessage}</span>}
        </section>
        <div className="product-master-actions">
          <div className="product-master-field-control" ref={fieldControlRef}>
            <button
              type="button"
              className={fieldPanelOpen ? 'is-popover-open' : ''}
              aria-expanded={fieldPanelOpen}
              aria-controls="sku-field-visibility-panel"
              onClick={() => {
                setFilterPanelOpen(false)
                setOpenFilterOptionsId(null)
                setFilterValueQuery('')
                setSortPanelOpen(false)
                setFieldPanelOpen((value) => !value)
              }}
            >
              <Columns3 aria-hidden="true" />
              字段显示
              <span className="product-master-field-count">{visibleColumnCount}/{SKU_COLUMNS.length}</span>
            </button>
            {fieldPanelOpen && (
              <div
                id="sku-field-visibility-panel"
                className="product-master-field-panel"
                role="dialog"
                aria-label="SKU字段显示设置"
              >
                <div className="product-master-field-panel-header">
                  <strong>字段显示</strong>
                  <span>已显示 {visibleColumnCount}/{SKU_COLUMNS.length}</span>
                </div>
                <div className="product-master-field-list">
                  {orderedColumns.map((column) => {
                    const visible = !hiddenColumns.has(column.id)
                    return (
                      <button
                        key={column.id}
                        type="button"
                        className={`product-master-field-row${visible ? '' : ' is-hidden'}${column.locked ? ' is-locked' : ''}`}
                        aria-pressed={visible}
                        disabled={column.locked}
                        onClick={() => toggleColumnVisibility(column)}
                      >
                        <span>{column.label}</span>
                        <span>
                          {column.locked && <small>固定</small>}
                          {visible ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}
                        </span>
                      </button>
                    )
                  })}
                </div>
                <div className="product-master-field-panel-footer">
                  <button type="button" onClick={() => setHiddenColumns(new Set())}>全部显示</button>
                  <button type="button" onClick={() => {
                    setHiddenColumns(defaultHiddenSkuColumns())
                    setColumnWidths({})
                    setColumnOrder([])
                  }}>
                    <RotateCcw aria-hidden="true" />恢复默认
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="product-master-filter-control" ref={filterControlRef}>
            <button
              type="button"
              className={[
                filterPanelOpen ? 'is-popover-open' : '',
                activeFilterConditions.length ? 'has-active-filters' : '',
              ].filter(Boolean).join(' ')}
              aria-expanded={filterPanelOpen}
              aria-controls="sku-filter-panel"
              onClick={toggleFilterPanel}
            >
              <ListFilter aria-hidden="true" />
              筛选
              {activeFilterConditions.length > 0 && (
                <span className="product-master-filter-count">{activeFilterConditions.length}</span>
              )}
            </button>
            {filterPanelOpen && (
              <div
                id="sku-filter-panel"
                className="product-master-filter-panel"
                role="dialog"
                aria-label="SKU筛选条件"
              >
                <div className="product-master-filter-header">
                  <div>
                    <strong>设置筛选条件</strong>
                    <span>多个条件同时满足</span>
                  </div>
                  {filterConditions.length > 0 && (
                    <button type="button" onClick={clearFilterConditions}>清空</button>
                  )}
                </div>
                <div className={`product-master-filter-list${openFilterOptionsId !== null ? ' has-open-options' : ''}`}>
                  {filterConditions.map((condition, index) => {
                    const filterType = filterTypeForColumn(condition.field)
                    const needsValue = filterOperatorNeedsValue(condition.operator)
                    const valueOptions = filterValueOptions[condition.field] || []
                    const isMultiSelect = MULTI_SELECT_FILTER_FIELDS.has(condition.field)
                    const selectedValues = splitFilterValues(condition.value)
                    return (
                      <div className="product-master-filter-row" key={condition.id}>
                        <span className="product-master-filter-join">{index === 0 ? '当' : '且'}</span>
                        <select
                          aria-label={`筛选字段 ${index + 1}`}
                          value={condition.field}
                          onChange={(event) => changeFilterField(condition.id, event.target.value as SkuColumnId)}
                        >
                          {SKU_COLUMNS.filter((column) => column.id !== 'rowNumber').map((column) => (
                            <option key={column.id} value={column.id}>{column.label}</option>
                          ))}
                        </select>
                        <select
                          aria-label={`筛选条件 ${index + 1}`}
                          value={condition.operator}
                          onChange={(event) => {
                            closeFilterValueOptions()
                            updateFilterCondition(condition.id, {
                              operator: event.target.value as SkuFilterOperator,
                            })
                          }}
                        >
                          {FILTER_OPERATORS[filterType].map((operator) => (
                            <option key={operator.value} value={operator.value}>{operator.label}</option>
                          ))}
                        </select>
                        {!needsValue ? (
                          <input
                            aria-label={`筛选值 ${index + 1}`}
                            value=""
                            disabled
                            placeholder="无需输入"
                          />
                        ) : valueOptions.length > 0 ? (
                          <div className="product-master-filter-value-picker">
                            <button
                              type="button"
                              className={`product-master-filter-value-trigger${(isMultiSelect ? selectedValues.length : condition.value) ? '' : ' is-placeholder'}`}
                              aria-label={`筛选值 ${index + 1}`}
                              aria-expanded={openFilterOptionsId === condition.id}
                              onClick={() => toggleFilterValueOptions(condition.id)}
                            >
                              <span>
                                {isMultiSelect
                                  ? (selectedValues.length ? selectedValues.join('、') : '请选择')
                                  : (condition.value || '请选择')}
                              </span>
                              <ChevronDown aria-hidden="true" />
                            </button>
                            {openFilterOptionsId === condition.id && (() => {
                              const query = filterValueQuery.trim().toLowerCase()
                              const visibleOptions = query
                                ? valueOptions.filter((option) => option.toLowerCase().includes(query))
                                : valueOptions
                              return (
                                <div className="product-master-filter-value-menu" role="group" aria-label={`筛选值选项 ${index + 1}`}>
                                  <div className="product-master-filter-value-search">
                                    <Search aria-hidden="true" />
                                    <input
                                      autoFocus
                                      aria-label={`搜索筛选值 ${index + 1}`}
                                      value={filterValueQuery}
                                      placeholder="搜索选项"
                                      onChange={(event) => setFilterValueQuery(event.target.value)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Escape') {
                                          event.stopPropagation()
                                          closeFilterValueOptions()
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="product-master-filter-value-options">
                                    {!isMultiSelect && (
                                      <button
                                        type="button"
                                        className={`product-master-filter-value-option${!condition.value ? ' is-selected' : ''}`}
                                        onClick={() => {
                                          updateFilterCondition(condition.id, { value: '' })
                                          closeFilterValueOptions()
                                        }}
                                      >
                                        请选择
                                      </button>
                                    )}
                                    {visibleOptions.map((option) => {
                                      if (isMultiSelect) {
                                        const checked = selectedValues.includes(option)
                                        return (
                                          <label key={option}>
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={() => {
                                                const nextValues = checked
                                                  ? selectedValues.filter((value) => value !== option)
                                                  : [...selectedValues, option]
                                                updateFilterCondition(condition.id, {
                                                  value: nextValues.join(FILTER_MULTI_VALUE_SEPARATOR),
                                                })
                                              }}
                                            />
                                            <span>{option}</span>
                                          </label>
                                        )
                                      }
                                      const selected = condition.value === option
                                      return (
                                        <button
                                          type="button"
                                          key={option}
                                          className={`product-master-filter-value-option${selected ? ' is-selected' : ''}`}
                                          onClick={() => {
                                            updateFilterCondition(condition.id, { value: option })
                                            closeFilterValueOptions()
                                          }}
                                        >
                                          {option}
                                        </button>
                                      )
                                    })}
                                    {visibleOptions.length === 0 && (
                                      <p className="product-master-filter-value-empty">没有匹配的选项</p>
                                    )}
                                  </div>
                                </div>
                              )
                            })()}
                          </div>
                        ) : (
                          <input
                            aria-label={`筛选值 ${index + 1}`}
                            type={filterType === 'number' ? 'number' : filterType === 'date' ? 'date' : 'text'}
                            value={condition.value}
                            placeholder="请输入"
                            onChange={(event) => updateFilterCondition(condition.id, { value: event.target.value })}
                          />
                        )}
                        <button
                          type="button"
                          className="product-master-filter-remove"
                          aria-label={`删除筛选条件 ${index + 1}`}
                          onClick={() => removeFilterCondition(condition.id)}
                        >
                          <X aria-hidden="true" />
                        </button>
                      </div>
                    )
                  })}
                  {filterConditions.length === 0 && (
                    <p className="product-master-filter-empty">尚未添加筛选条件。</p>
                  )}
                </div>
                <div className="product-master-filter-footer">
                  <button type="button" onClick={addFilterCondition}><Plus aria-hidden="true" />添加条件</button>
                  <span>命中 {filtered.length.toLocaleString('zh-CN')} 条</span>
                </div>
              </div>
            )}
          </div>
          <div className="product-master-sort-control" ref={sortControlRef}>
            <button
              type="button"
              className={[
                sortPanelOpen ? 'is-popover-open' : '',
                sortRule ? 'has-active-sort' : '',
              ].filter(Boolean).join(' ')}
              aria-expanded={sortPanelOpen}
              aria-controls="sku-sort-panel"
              onClick={toggleSortPanel}
            >
              <ArrowUpDown aria-hidden="true" />
              排序
              {sortRule && <span className="product-master-filter-count">1</span>}
            </button>
            {sortPanelOpen && (
              <div
                id="sku-sort-panel"
                className="product-master-sort-panel"
                role="dialog"
                aria-label="SKU排序设置"
              >
                <div className="product-master-sort-header">
                  <strong>设置排序</strong>
                  <span>当前支持单字段排序</span>
                </div>
                <div className="product-master-sort-fields">
                  <label>
                    <span>字段</span>
                    <select
                      aria-label="排序字段"
                      value={sortRule?.field || 'productName'}
                      onChange={(event) => setSortRule({
                        field: event.target.value as SkuColumnId,
                        direction: sortRule?.direction || 'asc',
                      })}
                    >
                      {SORTABLE_SKU_COLUMNS.map((column) => (
                        <option key={column.id} value={column.id}>{column.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>顺序</span>
                    <select
                      aria-label="排序顺序"
                      value={sortRule?.direction || 'asc'}
                      onChange={(event) => setSortRule({
                        field: sortRule?.field || 'productName',
                        direction: event.target.value as SkuSortDirection,
                      })}
                    >
                      <option value="asc">升序</option>
                      <option value="desc">降序</option>
                    </select>
                  </label>
                </div>
                <div className="product-master-sort-footer">
                  <span>{sortRule ? `${SKU_COLUMNS.find((column) => column.id === sortRule.field)?.label} · ${sortRule.direction === 'asc' ? '升序' : '降序'}` : '尚未设置排序'}</span>
                  <button type="button" disabled={!sortRule} onClick={() => setSortRule(null)}>清除排序</button>
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            title="全部可见列一键按表头文字宽度收紧，同屏展示更多列"
            onClick={() => applyAutoColumnWidths('header')}
          >
            <ChevronsRightLeft aria-hidden="true" />
            适应表头
          </button>
          <button
            type="button"
            title="全部可见列一键按当前已加载行的内容宽度调整"
            onClick={() => applyAutoColumnWidths('content')}
          >
            <ChevronsLeftRight aria-hidden="true" />
            适应内容
          </button>
          <button type="button" className={editUnlocked ? 'is-unlocked' : ''} onClick={toggleEditLock} disabled={saving} title={saving ? undefined : editUnlocked ? drafts.size ? `保存 ${drafts.size} 项修改并锁定店铺字段` : '点击锁定店铺字段' : '点击解锁店铺字段'}>
            {editUnlocked ? <LockOpen aria-hidden="true" /> : <Lock aria-hidden="true" />}
            {saving ? '正在保存…' : editUnlocked ? drafts.size ? `解锁中可编辑 · 保存并锁定（${drafts.size}）` : '解锁中可编辑' : '锁定中不可编辑'}
          </button>
        </div>
        <label className="search-control">
          <span className="sr-only">搜索商品资料</span><Search aria-hidden="true" />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索商品、SPU、京东SKU或商家SKU" />
        </label>
        </section>

        {error && <p className="form-error">{error}</p>}

        <div className="sku-master-table-shell">
        {summaryDock.visible && (
          <div
            className="sku-master-docked-summary"
            style={{ left: `${summaryDock.left}px`, width: `${summaryDock.width}px` }}
          >
            <table
              className="product-master-table sku-master-table"
              aria-label="固定底部统计"
              style={{
                minWidth: `${visibleTableWidth}px`,
                width: `${visibleTableWidth}px`,
                tableLayout: 'fixed',
                transform: `translateX(-${tableScrollLeft}px)`,
              }}
            >
              <colgroup>
                {visibleColumns.map((column) => (
                  <col key={column.id} style={{ width: `${columnWidth(column)}px` }} />
                ))}
              </colgroup>
              <tfoot className="sku-master-summary-foot">
                <tr>{renderSummaryCells('docked')}</tr>
              </tfoot>
            </table>
          </div>
        )}
        <div className="sku-master-sticky-header" style={{ top: `${stickyToolbarTop + stickyToolbarHeight}px` }}>
          <table
            className="product-master-table sku-master-table"
            aria-label="SKU表头"
            style={{
              minWidth: `${visibleTableWidth}px`,
              width: `${visibleTableWidth}px`,
              tableLayout: 'fixed',
              transform: `translateX(-${tableScrollLeft}px)`,
            }}
          >
            <colgroup>
              {visibleColumns.map((column) => (
                <col key={column.id} style={{ width: `${columnWidth(column)}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {visibleColumns.map((column) => {
                  const dropClass = columnDropTarget?.id === column.id ? ` is-column-drop-${columnDropTarget.position}` : ''
                  const headerSortable = HEADER_SORTABLE_SKU_FIELDS.has(column.id)
                  const sortActive = sortRule?.field === column.id
                  return <th
                    key={column.id}
                    data-column-id={column.id}
                    className={`${column.id === 'rowNumber' ? 'sku-master-rownumber-cell' : 'is-column-draggable'}${headerSortable ? ' is-column-sortable' : ''}${draggedColumnId === column.id ? ' is-column-dragging' : ''}${dropClass}`}
                    style={{ width: `${columnWidth(column)}px` }}
                    aria-grabbed={draggedColumnId === column.id}
                    aria-sort={sortActive ? (sortRule.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    title={column.id === 'rowNumber'
                      ? '固定列'
                      : headerSortable
                        ? '点击排序；按住拖动可调整字段顺序'
                        : '按住左右拖动调整字段顺序'}
                    onPointerDown={(event) => handleColumnPointerDown(event, column)}
                  >
                    {column.id === 'rowNumber' ? (
                      <span>{column.label}</span>
                    ) : <>
                      <span className="sku-column-label">
                        {column.label}
                        {headerSortable && (
                          <span className={`sku-column-sort-icon${sortActive ? ' is-active' : ''}`} aria-hidden="true">
                            {!sortActive
                              ? <ChevronsUpDown />
                              : sortRule.direction === 'asc' ? <ArrowUp /> : <ArrowDown />}
                          </span>
                        )}
                      </span>
                      <span
                        className="sku-column-resizer"
                        role="separator"
                        aria-label={`调整${column.label}列宽`}
                        aria-orientation="vertical"
                        tabIndex={0}
                        title="拖动调整列宽，双击恢复默认"
                        onPointerDown={(event) => startColumnResize(event, column)}
                        onKeyDown={(event) => resizeColumnWithKeyboard(event, column)}
                        onDoubleClick={() => resetColumnWidth(column.id)}
                      />
                    </>}
                  </th>
                })}
              </tr>
            </thead>
          </table>
        </div>
        <div
          ref={tableScrollRef}
          className="table-responsive product-master-table-wrap sku-master-table-wrap"
          onScroll={(event) => setTableScrollLeft(event.currentTarget.scrollLeft)}
        >
        <table
          className="product-master-table sku-master-table"
          style={{ minWidth: `${visibleTableWidth}px`, width: `${visibleTableWidth}px`, tableLayout: 'fixed' }}
        >
          <colgroup>
            {visibleColumns.map((column) => (
              <col key={column.id} style={{ width: `${columnWidth(column)}px` }} />
            ))}
          </colgroup>
          <thead className="sku-master-source-head" aria-hidden="true">
            <tr>
              {visibleColumns.map((column) => (
                <th key={column.id} className={column.id === 'rowNumber' ? 'sku-master-rownumber-cell' : ''} style={{ width: `${columnWidth(column)}px` }}>
                  <span>{column.label}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {virtualTopHeight > 0 && (
              <tr className="sku-virtual-spacer" aria-hidden="true">
                <td colSpan={visibleColumnCount} style={{ height: `${virtualTopHeight}px` }} />
              </tr>
            )}
            {virtualRows.map((sku, virtualRowIndex) => {
              const record = records.get(sku.id)
              const companyRecords = internalRecordsByCode.get(sku.merchantSku.trim()) || []
              const erpRecord = erpInventoryByProductCode.get(sku.merchantSku.trim())
              const isAccessory = accessoryBySku.get(sku.id) ?? false
              const isListed = listedBySku.get(sku.id) ?? false
              const inheritedJdWarehouse = isJdWarehouse(sku.id)
              const internalCost = internalCostsBySku.get(sku.id) || internalProductCost(companyRecords)
              const baseInternalCost = internalProductCost(companyRecords)
              const currentInventoryCostMode = currentInventoryCostModesBySku.get(sku.id) || baseInternalCost.inventoryCostMode
              const inventoryCostOperations = [...(record?.inventoryCostOperations || [])]
                .sort((left, right) => right.changedAt.localeCompare(left.changedAt))
              const inventoryHistoryTitle = inventoryCostOperations.length
                ? inventoryCostOperations.map((operation) => `${operation.changedAt}：${inventoryCostModeLabel(operation.previousMode)} → ${inventoryCostModeLabel(operation.mode)}`).join('\n')
                : '尚无人工切换记录；默认有品牌特价按特价库存，否则按总代库存。'
              const shippingProvider = shippingProviderFor(
                record,
                inheritedJdWarehouse,
                selectedDate,
                costDefaults,
                isAccessory,
                internalCost.isBrandSpecial,
              )
              const costVersion = costVersionOn(record, selectedDate)
              const rowDraft = editUnlocked
                ? drafts.get(sku.id) || draftFor(
                  sku,
                  record,
                  inheritedJdWarehouse,
                  selectedDate,
                  costDefaults,
                  isAccessory,
                  internalCost.isBrandSpecial,
                  isListed,
                )
                : null
              const cost = resolvedProductCost(
                record,
                selectedDate,
                costDefaults,
                rowDraft?.shippingProvider || shippingProvider,
                isAccessory || internalCost.isBrandSpecial,
              )
              const estimateRecord = rowDraft ? {
                skuId: sku.id,
                isAccessory,
                fulfillmentMode: rowDraft.fulfillmentMode,
                tagIds: rowDraft.tagIds,
                note: rowDraft.note,
                inventoryCostOperations: record?.inventoryCostOperations || [],
                costVersions: [{
                  id: `${sku.id}-preview`,
                  effectiveDate: selectedDate,
                  packagingCost: optionalNumber(rowDraft.packagingCost),
                  shippingProvider: rowDraft.shippingProvider,
                  shippingFee: optionalNumber(rowDraft.shippingFee),
                  platformFeeRate: optionalNumber(rowDraft.platformFeeRate),
                  taxRate: optionalNumber(rowDraft.taxRate),
                  updatedAt: '',
                }],
              } : record
              const estimate = estimatedProfitForSku(
                sku,
                estimateRecord,
                [selectedDate],
                costDefaults,
                companyRecords,
                rowDraft?.shippingProvider || shippingProvider,
              )
              const rebateRate = rebateRateFor(
                isAccessory,
                internalCost.isBrandSpecial,
                internalCost.productCost,
              )
              const rebateAmount = rebateAmountFor(
                internalCost.productCost,
                rebateRate,
                estimate.metrics.soldItemCount,
              )
              const profitWithRebate = profitWithRebateFor(
                estimate.profit,
                rebateAmount,
              )
              const profitWithRebateMargin = profitWithRebate !== null && estimate.metrics.gmv > 0
                ? profitWithRebate / estimate.metrics.gmv
                : null
              const isDirty = drafts.has(sku.id)
              return (
                <tr key={sku.id} className={isDirty ? 'is-editing' : ''}>
                  {columnVisible('rowNumber') && <td className="sku-master-rownumber-cell sku-row-number">{virtualStart + virtualRowIndex + 1}</td>}
                  {columnVisible('image') && <td className="master-image-cell">
                    {sku.image ? <img src={sku.image} alt="" loading="lazy" /> : <span className="master-image-fallback" title="请在公司产品库上传图片">无图</span>}
                  </td>}
                  {columnVisible('productName') && <td><strong className="master-product-name" title={sku.name}>{sku.name}</strong></td>}
                  {columnVisible('spu') && <td>{sku.spuId || '—'}</td>}
                  {columnVisible('jdSku') && <td className="sku-id-cell">
                    <a
                      href={`/product/sku/${sku.id}`}
                      target="_blank"
                      rel="noreferrer"
                      title="在新页面打开SKU分析"
                    >
                      {sku.id}
                    </a>
                    <button
                      type="button"
                      className={`sku-copy-button${copiedSkuId === `jd:${sku.id}` ? ' is-copied' : ''}`}
                      title={copiedSkuId === `jd:${sku.id}` ? '已复制' : '复制SKU'}
                      aria-label={`复制SKU ${sku.id}`}
                      onClick={() => copySkuId(sku.id, `jd:${sku.id}`)}
                    >
                      {copiedSkuId === `jd:${sku.id}` ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                    </button>
                  </td>}
                  {columnVisible('merchantSku') && <td className="sku-id-cell">
                    {sku.merchantSku
                      ? <>
                          {sku.merchantSku}
                          <button
                            type="button"
                            className={`sku-copy-button${copiedSkuId === `merchant:${sku.id}` ? ' is-copied' : ''}`}
                            title={copiedSkuId === `merchant:${sku.id}` ? '已复制' : '复制商家SKU'}
                            aria-label={`复制商家SKU ${sku.merchantSku}`}
                            onClick={() => copySkuId(sku.merchantSku, `merchant:${sku.id}`)}
                          >
                            {copiedSkuId === `merchant:${sku.id}` ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                          </button>
                        </>
                      : '—'}
                  </td>}
                  {columnVisible('soldItemCount') && <td className="numeric">{estimate.metrics.soldItemCount.toLocaleString('zh-CN')}</td>}
                  {columnVisible('erpAvailable') && <td className="numeric sku-erp-inventory"><span className={erpRecord?.available === 0 ? 'inventory-figure-danger' : ''} title={erpRecord ? `网店管家ERP可订购数量 · 货品 ${erpRecord.productCode}` : '未按商家SKU关联到ERP货品'}>{erpRecord ? erpRecord.available.toLocaleString('zh-CN') : '—'}</span></td>}
                  {columnVisible('companyProductName') && <td><span className="sku-company-value">{distinctValues(companyRecords, (company) => company.productName)}</span></td>}
                  {columnVisible('barcode') && <td><span className="sku-company-value">{distinctValues(companyRecords, (company) => company.barcode)}</span></td>}
                  {columnVisible('generalAgentPrice') && <td className="numeric">{internalPrice(companyRecords, 'generalAgentPrice')}</td>}
                  {columnVisible('brandSpecialPrice') && <td className="numeric"><span className={internalCost.issue === 'special-price-conflict' ? 'profit-missing' : 'master-derived-cost'} title="按特价0305、特价0225、首次品牌特价的顺序取第一个有效价格">{internalCost.issue === 'special-price-conflict' ? '特价冲突' : displayCost(internalCost.brandSpecialPrice)}</span></td>}
                  {columnVisible('productCost') && <td className="numeric"><span className={internalCost.productCost === null ? 'profit-missing' : 'master-derived-cost'} title={`按 ${selectedDate} 当天库存成本口径取价；历史单据不随今天的切换回算`}>{internalCost.issue === 'general-price-conflict' ? '总代价冲突' : internalCost.issue === 'special-price-conflict' ? '特价冲突' : displayCost(internalCost.productCost)}</span></td>}
                  {columnVisible('inventoryCostMode') && <td><span className={`inventory-cost-label ${currentInventoryCostMode === 'brand-special' ? 'is-special' : ''}`} title="公司产品库的当前库存成本口径，本店铺表中只读">{inventoryCostModeLabel(currentInventoryCostMode)}</span></td>}
                  {columnVisible('inventoryCostHistory') && <td><span className="inventory-history-label" title={inventoryHistoryTitle}>{inventoryCostOperations.length ? `${inventoryCostOperations.length} 条` : '无记录'}</span></td>}
                  {columnVisible('rebateRate') && <td className="numeric"><span title="非配件且没有品牌特价的SKU按产品成本20%计算返点">{rebateRate === null ? '—' : rebateRate.toFixed(1)}</span></td>}
                  {columnVisible('provincialAgentPrice') && <td className="numeric">{internalPrice(companyRecords, 'provincialAgentPrice')}</td>}
                  {columnVisible('ecommercePrice') && <td className="numeric">{internalPrice(companyRecords, 'ecommercePrice')}</td>}
                  {columnVisible('ip') && <td><span className="sku-company-value">{distinctValues(companyRecords, (company) => company.ip)}</span></td>}
                  {columnVisible('capacity') && <td className="numeric"><span className="sku-company-value">{distinctValues(companyRecords, (company) => company.capacity)}</span></td>}
                  {columnVisible('cupType') && <td><span className="sku-company-value">{distinctValues(companyRecords, (company) => company.cupType)}</span></td>}
                  {columnVisible('brand') && <td><span className="sku-company-value">{distinctValues(companyRecords, (company) => company.brand)}</span></td>}
                  {columnVisible('material') && <td><span className="sku-company-value">{distinctValues(companyRecords, (company) => company.material)}</span></td>}
                  {columnVisible('audience') && <td><span className="sku-company-value">{distinctValues(companyRecords, (company) => company.audience)}</span></td>}
                  {columnVisible('recordDate') && <td><span className="sku-company-value">{distinctValues(companyRecords, (company) => company.recordDate)}</span></td>}
                  {columnVisible('accessory') && <td><span className={`accessory-label ${isAccessory ? 'is-accessory' : ''}`} title="公司产品属性，本店铺表中只读">{isAccessory ? '是' : '否'}</span></td>}
                  {columnVisible('listed') && <td>{rowDraft ? <select aria-label={`${sku.id} 是否上架`} value={rowDraft.isListed ? 'yes' : 'no'} onChange={(event) => updateDraft(sku, { isListed: event.target.value === 'yes' })}><option value="yes">是</option><option value="no">否</option></select> : <span className={`listed-label ${isListed ? 'is-listed' : ''}`}>{isListed ? '是' : '否'}</span>}</td>}
                  {columnVisible('packagingCost') && <td>{rowDraft ? <input aria-label={`${sku.id} 包装成本`} type="number" min="0" step="0.01" value={rowDraft.packagingCost} onChange={(event) => updateDraft(sku, { packagingCost: event.target.value })} /> : displayCost(cost.packagingCost)}</td>}
                  {columnVisible('shippingProvider') && <td>{rowDraft ? <select
                    aria-label={`${sku.id} 快递方式`}
                    value={rowDraft.shippingProvider}
                    disabled={isAccessory || internalCost.isBrandSpecial}
                    onChange={(event) => {
                      const shippingProvider = event.target.value as ShippingProvider
                      updateDraft(sku, {
                        shippingProvider,
                        fulfillmentMode: shippingProvider === 'jd-warehouse' ? 'jd-warehouse' : 'merchant-direct',
                        shippingFee: defaultShippingFee(shippingProvider, costDefaults).toString(),
                      })
                    }}
                  ><option value="jd-warehouse">京东入仓</option><option value="jd-express">京东快递</option><option value="yto-express">圆通快递</option></select> : <span className={`shipping-label ${cost.shippingProvider === 'jd-warehouse' ? 'is-warehouse' : cost.shippingProvider === 'jd-express' ? 'is-jd' : 'is-yto'}`}>{shippingLabel(cost.shippingProvider)}</span>}</td>}
                  {columnVisible('shippingFee') && <td className="numeric"><span className="master-derived-cost" title="运费根据快递方式自动填写">{displayCost(rowDraft ? optionalNumber(rowDraft.shippingFee) : cost.shippingFee)}</span></td>}
                  {columnVisible('giftCost') && <td className="numeric"><span className="master-derived-cost" title="配件和品牌特价不送赠品，其他SKU赠品成本3元">{displayCost(cost.giftCost)}</span></td>}
                  {columnVisible('shippingInsuranceFee') && <td className="numeric"><span className="master-derived-cost" title="所有SKU统一计入1.5元运费险">{displayCost(cost.shippingInsuranceFee)}</span></td>}
                  {columnVisible('platformFeeRate') && <td>{rowDraft ? <input aria-label={`${sku.id} 平台扣点`} type="number" min="0" max="100" step="0.01" value={rowDraft.platformFeeRate} onChange={(event) => updateDraft(sku, { platformFeeRate: event.target.value })} /> : `${cost.platformFeeRate.toFixed(2)}%`}</td>}
                  {columnVisible('taxRate') && <td>{rowDraft ? <input aria-label={`${sku.id} 税点`} type="number" min="0" max="100" step="0.01" value={rowDraft.taxRate} onChange={(event) => updateDraft(sku, { taxRate: event.target.value })} /> : `${cost.taxRate.toFixed(2)}%`}</td>}
                  {columnVisible('tags') && <td>
                    {rowDraft ? (
                      <span className="master-tag-editor">
                        {productMaster.tags.map((tag) => (
                          <label key={tag.id}><input type="checkbox" checked={rowDraft.tagIds.includes(tag.id)} onChange={() => updateDraft(sku, { tagIds: rowDraft.tagIds.includes(tag.id) ? rowDraft.tagIds.filter((id) => id !== tag.id) : [...rowDraft.tagIds, tag.id] })} />{tag.name}</label>
                        ))}
                      </span>
                    ) : (
                      <span className="master-tag-list">{record?.tagIds.map((id) => {
                        const tag = productMaster.tags.find((item) => item.id === id)
                        return tag ? <span key={id} className={`tag-badge tag-${tag.tone}`}>{tag.name}</span> : null
                      })}{!record?.tagIds.length && '—'}</span>
                    )}
                  </td>}
                  {columnVisible('effectiveDate') && <td>{rowDraft ? <input aria-label={`${sku.id} 生效日期`} type="date" value={rowDraft.effectiveDate} onChange={(event) => updateDraft(sku, { effectiveDate: event.target.value })} /> : costVersion?.effectiveDate || '全局默认'}</td>}
                  {columnVisible('gmv') && <td>{money(estimate.metrics.gmv)}</td>}
                  {columnVisible('profit') && <td className={estimate.profit === null ? 'profit-missing' : estimate.profit >= 0 ? 'profit-positive' : 'profit-negative'}>{estimate.activeDates === 0 ? '无成交' : estimate.profit === null ? '缺少总代价' : money(estimate.profit)}</td>}
                  {columnVisible('rebateAmount') && <td className={rebateAmount === null ? 'profit-missing' : 'profit-positive'} title="返点金额 = 总代价 × 返点 × 成交件数">{rebateRate === null ? '—' : estimate.activeDates === 0 ? '无成交' : rebateAmount === null ? '缺少总代价' : money(rebateAmount)}</td>}
                  {columnVisible('profitWithRebate') && <td className={profitWithRebate === null ? 'profit-missing' : profitWithRebate >= 0 ? 'profit-positive' : 'profit-negative'}>{estimate.activeDates === 0 ? '无成交' : profitWithRebate === null ? '缺少总代价' : money(profitWithRebate)}</td>}
                  {columnVisible('profitWithRebateMargin') && <td className={profitWithRebateMargin === null ? 'profit-missing' : profitWithRebateMargin >= 0 ? 'profit-positive' : 'profit-negative'}>{estimate.activeDates === 0 ? '无成交' : profitWithRebateMargin === null ? '缺少总代价' : percent(profitWithRebateMargin * 100)}</td>}
                  {columnVisible('margin') && <td className={estimate.margin === null ? 'profit-missing' : estimate.margin >= 0 ? 'profit-positive' : 'profit-negative'}>{estimate.activeDates === 0 ? '无成交' : estimate.margin === null ? '缺少总代价' : percent(estimate.margin * 100)}</td>}
                </tr>
              )
            })}
            {virtualBottomHeight > 0 && (
              <tr className="sku-virtual-spacer" aria-hidden="true">
                <td colSpan={visibleColumnCount} style={{ height: `${virtualBottomHeight}px` }} />
              </tr>
            )}
            {!filtered.length && <tr><td colSpan={visibleColumnCount}><div className="empty-state">没有符合当前筛选条件的SKU。</div></td></tr>}
          </tbody>
          <tfoot className="sku-master-summary-foot" aria-hidden={summaryDock.visible}>
            <tr>{renderSummaryCells('source')}</tr>
          </tfoot>
        </table>
        </div>
        </div>
        </div>

        <div className="product-master-status">
        <span>全部 {filtered.length.toLocaleString('zh-CN')} 条 · 连续滚动，无分页</span>
        <span className={editUnlocked ? 'is-unlocked' : ''}>{editUnlocked ? drafts.size ? `编辑已解锁，${drafts.size} 个SKU待保存` : '编辑已解锁，尚无修改' : '编辑已锁定'}</span>
        </div>
    </div>
  )
}
